import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HrDashboardLayout from '../components/layouts/HrDashboardLayout';
import Button from '../components/atoms/Button';
import Icon from '../components/atoms/Icon';
import Input from '../components/atoms/Input';
import { createJob, getApplications, getJobs, updateJobStatus, updateJob, deleteApplication, updateApplicationStatus, deleteJob, getCandidateProfile, updateInterviewDetails } from '../services/api';
import { getMessages, sendMessage, markMessagesAsRead, getUnreadCount, getTotalUnreadForHR, getApplicationsWithUnreadMessages } from '../services/messaging';
import SessionManager from '../utils/sessionManager';

// Build headers with JWT Authorization from SessionManager
const buildAuthHeaders = (extra = {}) => {
  let session = SessionManager.getCurrentSession();
  if (!session || !session.access_token) {
    session = SessionManager.getSession('candidate') || SessionManager.getSession('hr');
  }
  const token = session?.access_token;
  return {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  };
};

// Company configuration
const COMPANY_CONFIG = {
  name: 'Aptirix',
  fullName: 'Aptirix Digital Services Pvt Ltd',
  website: 'https://aptirix.com',
};

// Default fallback data - can be replaced with API calls
const DEFAULT_DASHBOARD_DATA = {
  summary: [
    { label: 'Job Posts', value: 0, trend: '0 this week', icon: 'work', iconBg: 'bg-blue-100', iconColor: 'text-blue-600' },
    { label: 'Applications', value: 0, trend: '0 new', icon: 'description', iconBg: 'bg-green-100', iconColor: 'text-green-600' },
    { label: 'Under Review', value: 0, trend: '0 candidates', icon: 'visibility', iconBg: 'bg-orange-100', iconColor: 'text-orange-600' },
    { label: 'Interviews', value: 0, trend: '0 this week', icon: 'event', iconBg: 'bg-purple-100', iconColor: 'text-purple-600' },
  ],
  interviewSchedule: [],
  talentPool: [],
  messagingThreads: [],
  hrCourses: [],
  companyProfile: {
    name: COMPANY_CONFIG.name,
    website: COMPANY_CONFIG.website,
    logo: 'https://logo.clearbit.com/aptirix.com',
    address: 'RMZ Ecoworld, Outer Ring Road, Bengaluru, India',
    managers: [],
  },
  teamMembers: [],
  analyticsStats: [],
  recentActivity: [],
  notifications: [],
};

// Fallback jobs disabled to avoid confusion - only real jobs from the database will be shown
const fallbackCompanyJobs = [];

// Pipeline stages mapped to actual application statuses
const pipelineStages = [
  { label: 'Applied', status: 'applied' },
  { label: 'Under Review', status: 'review' },
  { label: 'Interview', status: 'interview' },
  { label: 'Offer', status: 'offer' },
  { label: 'Rejected', status: 'rejected' },
];

const initialJobForm = {
  title: '',
  location: '',
  description: '',
  employmentType: 'Full-time',
  workType: 'on-site',
  experienceLevel: 'mid',
  department: 'Product Engineering',
  category: 'IT',
  tags: '',
  jdPdfFile: null,
  // Interview Configuration (Optional)
  totalQuestions: 5,
  technicalQuestions: 3,
  behavioralQuestions: 1,
  situationalQuestions: 1,
  additionalRequirements: '', // Custom prompt for AI question generation
  // Auto-Rejection Configuration
  autoRejectEnabled: false,
  autoRejectThreshold: 70,
  // Live Interview Configuration
  liveInterviewEnabled: false,
  liveInterviewDuration: 5,
  liveInterviewLanguage: 'en-US',
  liveInterviewFocus: ['technical', 'behavioral'],
};

const initialInterviewForm = {
  date: '',
  startTime: '',
  timezone: 'Asia/Kolkata',
  meetingLink: '',
  meetingPlatform: 'Google Meet',
  panel: '',
  notes: '',
  location: '',
};

const HrDashboardPage = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [selectedApplicant, setSelectedApplicant] = useState(null);
  const [selectedCandidateProfile, setSelectedCandidateProfile] = useState(null);
  const [candidateProfileLoading, setCandidateProfileLoading] = useState(false);
  const [candidateProfileError, setCandidateProfileError] = useState('');
  const [loadingApplicants, setLoadingApplicants] = useState(true);
  const [jobStatusFilter, setJobStatusFilter] = useState('All');
  const [jobTypeFilter, setJobTypeFilter] = useState('All');
  const [selectedJobFilter, setSelectedJobFilter] = useState(null); // For applicant tracking job filter
  const [companyJobs, setCompanyJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [jobFormOpen, setJobFormOpen] = useState(false);
  const [jobForm, setJobForm] = useState(initialJobForm);
  const [jobSubmitting, setJobSubmitting] = useState(false);
  const [jobFormError, setJobFormError] = useState('');
  const [editingJob, setEditingJob] = useState(null);
  const [editJobForm, setEditJobForm] = useState(initialJobForm);
  const [editJobSubmitting, setEditJobSubmitting] = useState(false);
  const [editJobError, setEditJobError] = useState('');
  const [notes, setNotes] = useState(['Requested updated portfolio link', 'Share feedback with hiring manager']);
  const [noteDraft, setNoteDraft] = useState('');
  const [quickMessage, setQuickMessage] = useState('');
  const [deleteConfirmJob, setDeleteConfirmJob] = useState(null); // Job to be deleted (for confirmation modal)
  const [deleteConfirmApplication, setDeleteConfirmApplication] = useState(null); // Application to be deleted (for confirmation modal)
  const [interviewModalOpen, setInterviewModalOpen] = useState(false);
  const [interviewForm, setInterviewForm] = useState(initialInterviewForm);
  const [interviewSaving, setInterviewSaving] = useState(false);
  const [interviewError, setInterviewError] = useState('');
  
  // Dynamic dashboard data
  const [summaryStats, setSummaryStats] = useState(DEFAULT_DASHBOARD_DATA.summary);
  const [interviewSchedule, setInterviewSchedule] = useState(DEFAULT_DASHBOARD_DATA.interviewSchedule);
  const [talentPool, setTalentPool] = useState(DEFAULT_DASHBOARD_DATA.talentPool);
  const [messagingThreads, setMessagingThreads] = useState(DEFAULT_DASHBOARD_DATA.messagingThreads);
  const [hrCourses, setHrCourses] = useState(DEFAULT_DASHBOARD_DATA.hrCourses);
  const [companyProfile, setCompanyProfile] = useState(DEFAULT_DASHBOARD_DATA.companyProfile);
  const [teamMembers, setTeamMembers] = useState(DEFAULT_DASHBOARD_DATA.teamMembers);
  const [analyticsStats, setAnalyticsStats] = useState(DEFAULT_DASHBOARD_DATA.analyticsStats);
  const [recentActivity, setRecentActivity] = useState(DEFAULT_DASHBOARD_DATA.recentActivity);
  const [notifications, setNotifications] = useState(DEFAULT_DASHBOARD_DATA.notifications);
  const [loadingDashboard, setLoadingDashboard] = useState(true);
  const [activityHistory, setActivityHistory] = useState([]); // Track all activities
  const [chatModalOpen, setChatModalOpen] = useState(false);
  const [selectedChatCandidate, setSelectedChatCandidate] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [hrUnreadCount, setHrUnreadCount] = useState(0);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [apiKeyData, setApiKeyData] = useState(null);
  const [apiKeyLoading, setApiKeyLoading] = useState(false);
  const [apiKeyVisible, setApiKeyVisible] = useState(false);
  
  // Get HR company name from new session system
  const getHrCompanyName = () => {
    // Try to migrate legacy session first
    SessionManager.migrateLegacySession();
    
    const hrSession = SessionManager.getSession('hr');
    const companyName = hrSession?.company || 
                       hrSession?.profile?.company_name || 
                       COMPANY_CONFIG.name;
    
    console.log('🏢 HR Dashboard - Company Name Debug:', {
      hrSession,
      company: hrSession?.company,
      profile_company: hrSession?.profile?.company_name,
      final_company: companyName
    });
    
    return companyName;
  };
  
  const hrCompanyName = getHrCompanyName();
  
  // Check HR authentication
  useEffect(() => {
    const isLoggedInAsHr = SessionManager.isLoggedIn('hr');
    const currentSession = SessionManager.getCurrentSession();
    
    if (!isLoggedInAsHr || (currentSession && currentSession.role !== 'hr')) {
      navigate('/login');
      return;
    }
  }, [navigate]);

  // Helper function to format time ago
  const formatTimeAgo = (date) => {
    if (!date) return 'Just now';
    const now = new Date();
    const time = new Date(date);
    const diffInSeconds = Math.floor((now - time) / 1000);
    
    if (diffInSeconds < 60) return 'Just now';
    if (diffInSeconds < 3600) return `${Math.floor(diffInSeconds / 60)}m ago`;
    if (diffInSeconds < 86400) return `${Math.floor(diffInSeconds / 3600)}h ago`;
    if (diffInSeconds < 604800) return `${Math.floor(diffInSeconds / 86400)}d ago`;
    return time.toLocaleDateString();
  };

  const formatInterviewSlot = (details) => {
    if (!details) return 'Awaiting schedule';
    if (details.date && details.start_time) {
      return `${details.date} • ${details.start_time}${details.timezone ? ` (${details.timezone})` : ''}`;
    }
    if (details.scheduled_at) {
      try {
        return new Date(details.scheduled_at).toLocaleString();
      } catch (err) {
        return details.scheduled_at;
      }
    }
    return 'Awaiting schedule';
  };

  // Add activity to history
  const addActivity = (title, type, application = null) => {
    const activity = {
      id: Date.now().toString(),
      title,
      type,
      time: new Date().toISOString(),
      applicationId: application?.id,
      candidateName: application?.candidate_name,
      jobTitle: application?.job_title,
    };
    setActivityHistory(prev => [activity, ...prev].slice(0, 50)); // Keep last 50 activities
  };

  // Add notification
  const addNotification = (message, icon = 'notifications') => {
    const notification = {
      id: Date.now().toString(),
      message,
      icon,
      time: new Date().toISOString(),
      read: false,
    };
    setNotifications(prev => [notification, ...prev].slice(0, 20)); // Keep last 20 notifications
  };

  const refreshApplications = useCallback(async () => {
    setLoadingApplicants(true);
    try {
      const data = await getApplications({ company: hrCompanyName });
      
      // Check for new applications by comparing with current state
      setApplications(prevApplications => {
        const previousIds = new Set(prevApplications.map(app => app.id));
        const newApplications = data.filter(app => !previousIds.has(app.id));
        
        // Only add notifications if we had previous applications (not first load)
        if (prevApplications.length > 0 && newApplications.length > 0) {
          newApplications.forEach(app => {
            addNotification(
              `New application received from ${app.candidate_name} for ${app.job_title || 'position'}`,
              'person_add'
            );
            addActivity(
              `New application from ${app.candidate_name}`,
              'Application',
              app
            );
          });
        } else if (prevApplications.length === 0 && data.length > 0) {
          // First load - add notifications for recent applications (last 24 hours)
          const oneDayAgo = new Date();
          oneDayAgo.setDate(oneDayAgo.getDate() - 1);
          
          data
            .filter(app => {
              if (!app.applied_at) return false;
              return new Date(app.applied_at) >= oneDayAgo;
            })
            .forEach(app => {
              addNotification(
                `New application from ${app.candidate_name} for ${app.job_title || 'position'}`,
                'person_add'
              );
            });
        }
        
        return data;
      });
      
      setSelectedApplicant(prev => {
        // Keep current selection if it still exists, otherwise select first
        const currentExists = data.find(app => app.id === prev?.id);
        return currentExists || data[0] || null;
      });

      // Create messaging threads from applications
      const threads = data.slice(0, 5).map(app => ({
        candidate: app.candidate_name,
        candidateId: app.candidate_id,
        candidateEmail: app.candidate_email,
        role: app.job_title,
        snippet: `Applied for ${app.job_title} - ATS Score: ${app.ats_score}%`,
        unread: false,
        applicationId: app.id,
      }));
      setMessagingThreads([
        ...threads,
        { candidate: 'Suresh Kumar', role: 'Backend Engineer', snippet: 'Thanks for the update...', unread: true },
        { candidate: 'Aditi Jain', role: 'Product Manager', snippet: 'Sharing the updated documents...', unread: false },
        { candidate: 'Manish Singh', role: 'Data Analyst', snippet: 'Awaiting interview link...', unread: false },
      ]);
    } catch (error) {
      const fallback = [
        {
          id: 'app-1',
          candidate_id: 'cand-101',
          candidate_name: 'Priya Sharma',
          candidate_email: 'priya@example.com',
          job_id: 'job-nexasure-swe',
          job_title: 'Senior Backend Engineer (Payments)',
          company: hrCompanyName,
          ats_score: 86,
          resume_text: 'Product designer with 7 years exp...',
          status: 'Shortlisted',
          applied_at: new Date().toISOString(),
        },
        {
          id: 'app-2',
          candidate_id: 'cand-102',
          candidate_name: 'Rahul Verma',
          candidate_email: 'rahul@example.com',
          job_id: 'job-nexasure-swe',
          job_title: 'Frontend Engineer',
          company: hrCompanyName,
          ats_score: 72,
          resume_text: 'Frontend engineer (React/TS)...',
          status: 'Applied',
          applied_at: new Date().toISOString(),
        },
      ];
      setApplications(fallback);
      setSelectedApplicant(fallback[0]);
    } finally {
      setLoadingApplicants(false);
    }
  }, [hrCompanyName]);

  useEffect(() => {
    refreshApplications();
    
    // Auto-refresh applications every 30 seconds to show new applications
    const interval = setInterval(() => {
      refreshApplications();
    }, 30000); // 30 seconds
    
    return () => clearInterval(interval);
  }, [refreshApplications]);

  const normalizeJob = (job) => {
    const rawCategory = `${job.category || ''} ${job.department || ''}`.toLowerCase();
    const inferredCategory =
      job.category === 'IT' || job.category === 'Non-IT'
        ? job.category
        : rawCategory.match(/finance|people|talent|marketing|sales|ops|hr/)
          ? 'Non-IT'
          : 'IT';

    const employmentType = job.type || job.employmentType || 'Full-time';
    const workType = job.work_type || job.workType || job.jobType || 'on-site';
    const experienceLevel = job.experience_level || job.experienceLevel || 'mid';
    const tagsArray = Array.isArray(job.tags)
      ? job.tags
      : typeof job.tags === 'string'
        ? job.tags.split(',').map((tag) => tag.trim()).filter(Boolean)
        : [];

    return {
      id: job.id || job._id || job.slug || job.title,
      title: job.title || job.role || 'Untitled role',
      status: job.status || (job.is_open ? 'Active' : 'Closed'),
      category: inferredCategory,
      department: job.department || job.role_category || (inferredCategory === 'IT' ? 'Product & Tech' : 'Business Ops'),
      jobType: workType,
      employmentType,
      workType,
      experienceLevel,
      location: job.location || job.city || job.work_location || 'Remote',
      experience: job.experience || `${experienceLevel} level`,
      applicants: job.applications_count || job.applications?.length || 0,
      views: job.views || job.impressions || 0,
      updated: job.updated_at
        ? new Date(job.updated_at).toLocaleDateString()
        : job.posted_at
          ? new Date(job.posted_at).toLocaleDateString()
          : 'Recently',
      company: (job.company || job.company_name || '').trim() || hrCompanyName,
      description: job.description || '',
      tags: tagsArray,
      jd_pdf_url: job.jd_pdf_url || job.jdPdfUrl || null,
    };
  };

  const refreshJobs = useCallback(async () => {
    setJobsLoading(true);
    try {
      const jobsResponse = await getJobs({ company: hrCompanyName });
      console.log('📋 Jobs API Response:', jobsResponse);
      console.log('🏢 HR Company Name:', hrCompanyName);
      
      const normalized = (jobsResponse || []).map(normalizeJob);
      console.log('✅ Normalized Jobs:', normalized);
      
      const scopedJobs = normalized.filter(
        (job) => job.company.toLowerCase() === hrCompanyName.toLowerCase()
      );
      console.log('🎯 Scoped Jobs (filtered):', scopedJobs);

      if (scopedJobs.length) {
        setCompanyJobs(scopedJobs);
      } else {
        // No jobs found for this company - show empty state
        console.warn('⚠️ No jobs found after filtering. Showing all jobs instead.');
        // If no jobs match after filtering, show all jobs from the response
        setCompanyJobs(normalized);
      }
    } catch (error) {
      console.error('Error loading jobs:', error);
      setCompanyJobs([]);
    } finally {
      setJobsLoading(false);
    }
  }, [hrCompanyName]);

  useEffect(() => {
    refreshJobs();
  }, [refreshJobs]);

  // Load dashboard data dynamically
  useEffect(() => {
    const loadDashboardData = async () => {
      setLoadingDashboard(true);
      try {
        // TODO: Replace with actual API calls
        // Example: const dashboardData = await getDashboardData({ company: hrCompanyName });
        
        // For now, using default/calculated data
        // Calculate summary stats from actual data dynamically
        const activeJobs = companyJobs.filter(job => job.status === 'Active').length;
        const totalApplications = applications.length;
        
        // Count applications by status dynamically
        const reviewCount = applications.filter(app => 
          (app.status || '').toLowerCase() === 'review'
        ).length;
        const interviewCount = applications.filter(app => 
          (app.status || '').toLowerCase() === 'interview'
        ).length;
        const offerCount = applications.filter(app => 
          (app.status || '').toLowerCase() === 'offer'
        ).length;
        
        // Calculate new applications (applied in last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const newApplications = applications.filter(app => {
          if (!app.applied_at) return false;
          const appliedDate = new Date(app.applied_at);
          return appliedDate >= sevenDaysAgo;
        }).length;
        
        // Load interview schedule dynamically from applications in interview stage
        const loadedInterviews = applications
          .filter(app => (app.status || '').toLowerCase() === 'interview')
          .map(app => ({
            id: app.id,
            candidate: app.candidate_name,
            role: app.job_title || 'N/A',
            time: formatInterviewSlot(app.interview_details),
            panel: (app.interview_details?.panel && app.interview_details.panel.length > 0)
              ? app.interview_details.panel
              : ['Panel TBD'],
            status: app.interview_details?.meeting_link ? 'Scheduled' : 'Pending Details',
            meetingLink: app.interview_details?.meeting_link,
          }));
        setInterviewSchedule(loadedInterviews);

        setSummaryStats([
          {
            label: 'Job Posts',
            value: companyJobs.length,
            trend: `${activeJobs} active`,
            icon: 'work', iconBg: 'bg-blue-100', iconColor: 'text-blue-600'
          },
          {
            label: 'Applications',
            value: totalApplications,
            trend: `${newApplications > 0 ? '+' : ''}${newApplications} new`,
            icon: 'description', iconBg: 'bg-green-100', iconColor: 'text-green-600'
          },
          {
            label: 'Under Review',
            value: reviewCount,
            trend: `${reviewCount > 0 ? reviewCount : 0} candidates`,
            icon: 'visibility', iconBg: 'bg-orange-100', iconColor: 'text-orange-600'
          },
          {
            label: 'Interviews',
            value: interviewCount,
            trend: `${interviewCount} scheduled`,
            icon: 'event', iconBg: 'bg-purple-100', iconColor: 'text-purple-600'
          },
        ]);

        // Load other dashboard sections (can be replaced with API calls)
        setTalentPool([
          { name: 'Ananya Rao', role: 'UI Lead', tags: ['Starred', 'Portfolio'], updated: 'Jul 12' },
          { name: 'Karan Patel', role: 'DevOps', tags: ['Shortlisted', 'AWS'], updated: 'Jul 09' },
          { name: 'Divya Menon', role: 'HRBP', tags: ['Talent Pool'], updated: 'Jul 02' },
        ]);

        setHrCourses([
          { title: 'AI Resume Screening Basics', status: 'Recommended', progress: '—' },
          { title: 'Inclusive Hiring Playbook', status: 'In Progress', progress: '45%' },
          { title: 'Advanced Interviewing Skills', status: 'Completed', progress: '100%' },
        ]);

        setCompanyProfile({
          name: hrCompanyName,
          website: COMPANY_CONFIG.website,
          logo: `https://logo.clearbit.com/${COMPANY_CONFIG.website.replace('https://', '').replace('http://', '')}`,
          address: 'RMZ Ecoworld, Outer Ring Road, Bengaluru, India',
          managers: ['Kavya Deshmukh', 'Rohan Mukherjee', 'Sana Qureshi'],
        });

        setTeamMembers([
          { name: 'Arjun Mehta', role: 'Recruiter', access: 'Admin' },
          { name: 'Ritika Saha', role: 'Hiring Manager', access: 'Recruiter' },
          { name: 'Sameer Kulkarni', role: 'Interviewer', access: 'Interviewer' },
        ]);

        setAnalyticsStats([
          { label: 'Time to Hire', value: '32 days' },
          { label: 'Offer Acceptance', value: '78%' },
          { label: 'Diversity Ratio', value: '48% F • 52% M' },
          { label: 'Source Split', value: 'LinkedIn 42%, Referral 30%, Portal 28%' },
        ]);

        // Generate dynamic recent activity from activity history
        const sortedActivity = [...activityHistory]
          .sort((a, b) => new Date(b.time) - new Date(a.time))
          .slice(0, 10)
          .map(activity => ({
            title: activity.title,
            type: activity.type,
            time: formatTimeAgo(activity.time),
          }));
        
        setRecentActivity(sortedActivity.length > 0 ? sortedActivity : [
          { title: 'No recent activity', type: 'System', time: '—' },
        ]);
      } catch (error) {
        console.error('Error loading dashboard data:', error);
        // Keep default values on error
      } finally {
        setLoadingDashboard(false);
      }
    };

    loadDashboardData();
  }, [companyJobs, applications, hrCompanyName, activityHistory]);

  const handleJobFormChange = (field, value) => {
    setJobForm((prev) => ({ ...prev, [field]: value }));
  };

  const closeJobForm = () => {
    setJobForm(initialJobForm);
    setJobFormError('');
    setJobFormOpen(false);
  };

  // Convert PDF file to base64 data URI
  const convertFileToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64String = reader.result;
        resolve(base64String);
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  };

  const handleCreateJob = async (event) => {
    event.preventDefault();
    if (!jobForm.title.trim() || !jobForm.location.trim() || !jobForm.description.trim()) {
      setJobFormError('Please fill in the title, location, and description.');
      return;
    }
    setJobSubmitting(true);
    setJobFormError('');
    try {
      // Convert JD PDF to base64 if provided
      let jdPdfUrl = null;
      if (jobForm.jdPdfFile) {
        try {
          jdPdfUrl = await convertFileToBase64(jobForm.jdPdfFile);
        } catch (error) {
          console.error('Error converting JD PDF:', error);
          setJobFormError('Failed to process JD PDF. Please try again.');
          setJobSubmitting(false);
          return;
        }
      }

      const newJob = await createJob({
        title: jobForm.title.trim(),
        company: hrCompanyName,
        location: jobForm.location.trim(),
        description: jobForm.description.trim(),
        type: jobForm.employmentType,
        work_type: jobForm.workType,
        experience_level: jobForm.experienceLevel,
        tags: [
          jobForm.department.trim(),
          jobForm.category.trim(),
          ...jobForm.tags.split(',').map((tag) => tag.trim()),
        ].filter(Boolean),
        jd_pdf_url: jdPdfUrl,
        // Interview Configuration
        interview_config: {
          total_questions: jobForm.totalQuestions,
          technical_questions: jobForm.technicalQuestions,
          behavioral_questions: jobForm.behavioralQuestions,
          situational_questions: jobForm.situationalQuestions,
          additional_requirements: jobForm.additionalRequirements.trim(),
        },
        // Auto-Rejection Configuration
        auto_reject_enabled: jobForm.autoRejectEnabled,
        auto_reject_threshold: jobForm.autoRejectThreshold,
        // Live Interview Configuration
        live_interview_enabled: jobForm.liveInterviewEnabled,
        live_interview_config: {
          duration_minutes: jobForm.liveInterviewDuration,
          language: jobForm.liveInterviewLanguage,
          focus_areas: jobForm.liveInterviewFocus,
        },
      });
      
      // Track activity
      addActivity(
        `New job post created: ${jobForm.title.trim()}`,
        'Job Post'
      );
      addNotification(
        `Job post "${jobForm.title.trim()}" has been published`,
        'work'
      );
      
      closeJobForm();
      refreshJobs();
    } catch (error) {
      setJobFormError(error.message || 'Unable to create job. Please try again.');
    } finally {
      setJobSubmitting(false);
    }
  };

  const openEditJobModal = (job) => {
    setEditingJob(job);
    // Map job fields to form fields - use API field names
    const workTypeMap = {
      'on-site': 'on-site',
      'onsite': 'on-site',
      'On-site': 'on-site',
      'hybrid': 'hybrid',
      'Hybrid': 'hybrid',
      'remote': 'remote',
      'Remote': 'remote',
    };
    const workType = workTypeMap[job.work_type || job.workType || job.jobType] || 'on-site';
    
    const experienceLevel = job.experience_level || job.experienceLevel || 'mid';
    
    setEditJobForm({
      title: job.title || '',
      location: job.location || '',
      description: job.description || '',
      employmentType: job.type || job.employmentType || 'Full-time',
      workType: workType,
      experienceLevel: experienceLevel,
      department: job.department || '',
      category: job.category || 'IT',
      tags: Array.isArray(job.tags) ? job.tags.join(', ') : (job.tags || ''),
      jdPdfFile: null, // Reset file input when opening edit modal
      // Interview config
      totalQuestions: job.interview_config?.total_questions || 5,
      technicalQuestions: job.interview_config?.technical_questions || 3,
      behavioralQuestions: job.interview_config?.behavioral_questions || 1,
      situationalQuestions: job.interview_config?.situational_questions || 1,
      additionalRequirements: job.interview_config?.additional_requirements || '',
    });
    setEditJobError('');
  };

  const closeEditJobModal = () => {
    setEditingJob(null);
    setEditJobForm(initialJobForm);
    setEditJobError('');
  };

  const handleEditJobFormChange = (field, value) => {
    setEditJobForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleUpdateJob = async (event) => {
    event.preventDefault();
    if (!editJobForm.title.trim() || !editJobForm.location.trim() || !editJobForm.description.trim()) {
      setEditJobError('Please fill in the title, location, and description.');
      return;
    }
    setEditJobSubmitting(true);
    setEditJobError('');
    try {
      // Update job in the database
      const updatePayload = {
        title: editJobForm.title.trim(),
        company: hrCompanyName,
        location: editJobForm.location.trim(),
        description: editJobForm.description.trim(),
        type: editJobForm.employmentType,
        work_type: editJobForm.workType,
        experience_level: editJobForm.experienceLevel,
        tags: Array.isArray(editJobForm.tags) ? editJobForm.tags : (editJobForm.tags ? editJobForm.tags.split(',').map(t => t.trim()).filter(Boolean) : []),
      };
      
      // Add interview config if it exists
      if (editJobForm.totalQuestions) {
        updatePayload.interview_config = {
          total_questions: editJobForm.totalQuestions,
          technical_questions: editJobForm.technicalQuestions,
          behavioral_questions: editJobForm.behavioralQuestions,
          situational_questions: editJobForm.situationalQuestions,
          additional_requirements: editJobForm.additionalRequirements || '',
        };
      }
      
      await updateJob(editingJob.id, updatePayload);
      
      // Update local state
      const updatedJobs = companyJobs.map(job => 
        job.id === editingJob.id 
          ? {
              ...job,
              title: editJobForm.title.trim(),
              location: editJobForm.location.trim(),
              description: editJobForm.description.trim(),
              jobType: editJobForm.workType,
              department: editJobForm.department.trim(),
              category: editJobForm.category,
            }
          : job
      );
      setCompanyJobs(updatedJobs);
      
      // Track activity
      addActivity(
        `Job post updated: ${editingJob.title}`,
        'Job Post'
      );
      
      closeEditJobModal();
      // Refresh jobs to get latest data from API
      refreshJobs();
    } catch (error) {
      setEditJobError(error.message || 'Unable to update job. Please try again.');
    } finally {
      setEditJobSubmitting(false);
    }
  };

  const handleJobStatusToggle = async (job) => {
    try {
      const newStatus = job.status === 'Active' ? 'Closed' : 'Active';
      
      // Update job status in the database
      await updateJobStatus(job.id, newStatus);
      
      // Update local state immediately for instant feedback
      const updatedJobs = companyJobs.map(j => {
        if (j.id === job.id) {
          return { ...j, status: newStatus };
        }
        return j;
      });
      setCompanyJobs(updatedJobs);
      
      // Track activity
      addActivity(
        `Job "${job.title}" status changed to ${newStatus}`,
        'Job Post'
      );
      
      // Refresh jobs from backend to ensure consistency
      setTimeout(() => {
        refreshJobs();
      }, 500);
    } catch (error) {
      console.error('Error toggling job status:', error);
      alert('Failed to update job status. Please try again.');
    }
  };

  const handleDeleteJob = (job) => {
    // Show confirmation modal
    setDeleteConfirmJob(job);
  };

  const confirmDeleteJob = async () => {
    if (!deleteConfirmJob) return;
    
    const job = deleteConfirmJob;
    try {
      // Delete from backend
      await deleteJob(job.id);

      // Update local state immediately
      const updatedJobs = companyJobs.filter(j => j.id !== job.id);
      setCompanyJobs(updatedJobs);
      
      // Track activity
      addActivity(
        `Job post deleted: ${job.title}`,
        'Job Post'
      );
      
      // Close modal
      setDeleteConfirmJob(null);
      
      // Refresh from backend to ensure consistency
      refreshJobs();
    } catch (error) {
      console.error('Error deleting job:', error);
      alert('Failed to delete job. Please try again.');
      setDeleteConfirmJob(null);
    }
  };

  const cancelDeleteJob = () => {
    setDeleteConfirmJob(null);
  };

  const filteredJobs = companyJobs.filter((job) => {
    const matchesStatus =
      jobStatusFilter === 'All' || job.status?.toLowerCase() === jobStatusFilter.toLowerCase();
    const matchesType = jobTypeFilter === 'All' || job.category === jobTypeFilter;
    return matchesStatus && matchesType;
  });

  // Calculate dynamic pipeline stats from actual applications (excluding auto-rejected)
  const pipelineStats = pipelineStages.map((stage) => {
    const count = applications.length
      ? applications.filter((app) => {
          // Exclude auto-rejected candidates from pipeline stats
          if (app.status === 'rejected' && app.auto_rejected) {
            return false;
          }
          
          const appStatus = (app.status || '').toLowerCase();
          return appStatus === stage.status.toLowerCase();
        }).length
      : 0;
    return {
      stage: stage.label,
      status: stage.status,
      count,
    };
  });

  const totalPipelineCount = pipelineStats.reduce((sum, entry) => sum + entry.count, 0) || 1;
  
  // Calculate additional dynamic metrics (excluding auto-rejected)
  const totalApplications = applications.filter(app => !(app.status === 'rejected' && app.auto_rejected)).length;
  const activeApplications = applications.filter(app => {
    if (app.status === 'rejected' && app.auto_rejected) return false;
    return ['applied', 'review', 'interview', 'offer'].includes((app.status || '').toLowerCase());
  }).length;
  const rejectedCount = applications.filter(app => 
    (app.status || '').toLowerCase() === 'rejected' && !app.auto_rejected
  ).length;
  const averageATSScore = applications.length > 0
    ? Math.round(
        applications
          .filter(app => app.ats_score != null && !(app.status === 'rejected' && app.auto_rejected))
          .reduce((sum, app) => sum + (app.ats_score || 0), 0) /
        applications.filter(app => app.ats_score != null && !(app.status === 'rejected' && app.auto_rejected)).length
      )
    : 0;

  const handleAddNote = () => {
    if (!noteDraft.trim()) return;
    setNotes([noteDraft.trim(), ...notes]);
    setNoteDraft('');
  };

  const handleSendQuickMessage = () => {
    if (!quickMessage.trim() || !selectedApplicant) return;
    
    // Add message to chat
    const newMessage = {
      id: Date.now(),
      sender: 'HR',
      text: quickMessage,
      timestamp: new Date().toISOString(),
    };
    
    setChatMessages(prev => [...prev, newMessage]);
    setQuickMessage('');
    
    addNotification(`Message sent to ${selectedApplicant.candidate_name}`, 'send');
  };

  const handleOpenChat = (thread) => {
    setSelectedChatCandidate(thread);
    
    // Load existing messages from localStorage
    const applicationId = thread.applicationId || selectedApplicant?.id;
    console.log('🔍 Opening chat for application:', applicationId);
    
    if (applicationId) {
      getMessages(applicationId).then(messages => {
        console.log('💬 Loaded messages:', messages.length, messages);
        setChatMessages(messages);
      });
      markMessagesAsRead(applicationId, 'Candidate');
    } else {
      console.error('❌ No application ID found!', { thread, selectedApplicant });
    }
    
    setChatModalOpen(true);
  };

  const handleSendChatMessage = async () => {
    if (!chatInput.trim()) return;

    const applicationId = selectedChatCandidate?.applicationId || selectedApplicant?.id;
    if (!applicationId) {
      console.error('❌ No application ID for sending message!');
      return;
    }

    const hrName = localStorage.getItem('dutlaunch_name') || 'HR Team';
    await sendMessage(applicationId, 'HR', chatInput, hrName);
    setChatInput('');

    const messages = await getMessages(applicationId);
    setChatMessages(messages);

    addNotification(`Message sent to ${selectedChatCandidate?.candidate || selectedApplicant?.candidate_name}`, 'send');
    updateMessagingThreads();
  };

  const updateMessagingThreads = async () => {
    // Update unread count for HR
    const totalUnread = await getTotalUnreadForHR();
    setHrUnreadCount(totalUnread);

    // Update messaging threads with unread indicators
    const appsWithUnread = await getApplicationsWithUnreadMessages();
    
    // Update existing threads with unread status
    setMessagingThreads(prev => {
      return prev.map(thread => {
        const unreadInfo = appsWithUnread.find(app => app.applicationId === thread.applicationId);
        if (unreadInfo) {
          return {
            ...thread,
            unread: true,
            unreadCount: unreadInfo.unreadCount,
            snippet: unreadInfo.lastMessage.text,
          };
        }
        return { ...thread, unread: false };
      });
    });
  };

  // Fetch messaging threads once on mount (no polling)
  useEffect(() => {
    updateMessagingThreads();
  }, []);

  const handleSendOffer = (thread) => {
    alert(`Sending offer to ${thread.candidate}`);
    addNotification(`Offer sent to ${thread.candidate}`, 'mail');
  };

  const handleRequestDocuments = (thread) => {
    alert(`Requesting documents from ${thread.candidate}`);
    addNotification(`Document request sent to ${thread.candidate}`, 'description');
  };

  const handleDeleteApplication = (application) => {
    // Show confirmation modal
    setDeleteConfirmApplication(application);
  };

  const confirmDeleteApplication = async () => {
    if (!deleteConfirmApplication) return;
    
    const application = deleteConfirmApplication;
    try {
      // Delete from database
      await deleteApplication(application.id);
      
      // Update local state
      const updatedApplications = applications.filter(app => app.id !== application.id);
      setApplications(updatedApplications);
      if (selectedApplicant?.id === application.id) {
        setSelectedApplicant(updatedApplications[0] || null);
      }
      
      // Track activity
      addActivity(
        `Application from ${application.candidate_name} was deleted`,
        'Application',
        application
      );
      
      // Close modal
      setDeleteConfirmApplication(null);
      
      // Refresh applications to ensure consistency
      refreshApplications();
    } catch (error) {
      console.error('Error deleting application:', error);
      alert('Failed to delete application. Please try again.');
      setDeleteConfirmApplication(null);
    }
  };

  const cancelDeleteApplication = () => {
    setDeleteConfirmApplication(null);
  };

  const handleUpdateApplicationStatus = async (applicationId, newStatus) => {
    try {
      const application = applications.find(app => app.id === applicationId);
      const oldStatus = application?.status;
      
      await updateApplicationStatus(applicationId, newStatus);
      const updatedApplications = applications.map(app => 
        app.id === applicationId ? { ...app, status: newStatus } : app
      );
      setApplications(updatedApplications);
      const updatedApplication = updatedApplications.find(app => app.id === applicationId) || null;
      if (selectedApplicant?.id === applicationId) {
        setSelectedApplicant(updatedApplication);
      }
      
      // Track activity
      const statusLabels = {
        'applied': 'Applied',
        'review': 'Under Review',
        'interview': 'Interview',
        'offer': 'Offer',
        'rejected': 'Rejected',
      };
      
      if (application) {
        addActivity(
          `${application.candidate_name} moved to ${statusLabels[newStatus] || newStatus}`,
          'Pipeline',
          application
        );
        
        if (newStatus === 'interview') {
          addNotification(
            `Interview scheduled for ${application.candidate_name} - ${application.job_title || 'position'}`,
            'event_available'
          );
        } else if (newStatus === 'offer') {
          addNotification(
            `Offer sent to ${application.candidate_name} for ${application.job_title || 'position'}`,
            'check_circle'
          );
        } else if (newStatus === 'rejected') {
          addNotification(
            `Application from ${application.candidate_name} was rejected`,
            'close'
          );
        }
      }
      
      refreshApplications();
      return updatedApplication;
    } catch (error) {
      console.error(`Error updating application status to ${newStatus}:`, error);
      alert(`Failed to update application status. Please try again.`);
      return null;
    }
  };

  const handleAcceptApplication = async (applicationId) => {
    await handleUpdateApplicationStatus(applicationId, 'review');
  };

  const handleMoveToInterview = async (applicationId) => {
    const updatedApp = await handleUpdateApplicationStatus(applicationId, 'interview');
    if (updatedApp) {
      openInterviewModal(updatedApp);
    }
  };

  const handleMoveToOffer = async (applicationId) => {
    await handleUpdateApplicationStatus(applicationId, 'offer');
  };

  const handleRejectApplication = async (applicationId) => {
    await handleUpdateApplicationStatus(applicationId, 'rejected');
  };

  const handleApplicationAction = async (applicationId, action) => {
    try {
      if (action === 'delete') {
        // This should not be called directly - use handleDeleteApplication instead
        const app = applications.find(a => a.id === applicationId);
        if (app) {
          handleDeleteApplication(app);
        }
      } else if (action === 'accept') {
        await handleAcceptApplication(applicationId);
      } else if (action === 'reject') {
        await handleRejectApplication(applicationId);
      } else if (action === 'close') {
        await handleRejectApplication(applicationId);
      } else if (action === 'open') {
        await updateApplicationStatus(applicationId, 'applied');
        const updatedApplications = applications.map(app => 
          app.id === applicationId ? { ...app, status: 'applied' } : app
        );
        setApplications(updatedApplications);
        if (selectedApplicant?.id === applicationId) {
          setSelectedApplicant(updatedApplications.find(app => app.id === applicationId));
        }
        refreshApplications();
      }
    } catch (error) {
      console.error('Error handling application action:', error);
    }
  };

  useEffect(() => {
    let isCancelled = false;
    const loadCandidateProfile = async () => {
      if (!selectedApplicant) {
        setSelectedCandidateProfile(null);
        setCandidateProfileError('');
        return;
      }
      const candidateId = selectedApplicant.candidate_id;
      const email = selectedApplicant.candidate_email;
      if (!candidateId && !email) {
        setSelectedCandidateProfile(null);
        setCandidateProfileError('');
        return;
      }
      setCandidateProfileLoading(true);
      setCandidateProfileError('');
      try {
        const profile = await getCandidateProfile(candidateId, email);
        if (!isCancelled) {
          setSelectedCandidateProfile(profile);
        }
      } catch (error) {
        console.error('Failed to load candidate profile:', error);
        if (!isCancelled) {
          setSelectedCandidateProfile(null);
          setCandidateProfileError('Unable to load candidate profile');
        }
      } finally {
        if (!isCancelled) {
          setCandidateProfileLoading(false);
        }
      }
    };
    loadCandidateProfile();
    return () => {
      isCancelled = true;
    };
  }, [selectedApplicant]);

  const candidateSkills = Array.isArray(selectedCandidateProfile?.skills) && selectedCandidateProfile.skills.length
    ? selectedCandidateProfile.skills
    : Array.isArray(selectedApplicant?.skills) && selectedApplicant.skills.length
      ? selectedApplicant.skills
      : ['Product Strategy', 'UX Research', 'Design Systems'];

  const profileName = selectedCandidateProfile?.full_name?.trim() || selectedApplicant?.candidate_name || 'Candidate';
  const profileEmail = selectedCandidateProfile?.email || selectedApplicant?.candidate_email || 'Email not provided';
  const profilePhone = selectedCandidateProfile?.phone || selectedCandidateProfile?.phone_number || 'Phone not provided';
  const profileRole =
    selectedCandidateProfile?.job_role ||
    selectedApplicant?.job_role ||
    selectedApplicant?.job_title ||
    'Role not specified';
  const resumeUrl = selectedApplicant?.resume_url || selectedCandidateProfile?.resume_url;
  const resumeSummary = selectedApplicant?.resume_text || 'Resume text unavailable';
  const resumeFilename = selectedCandidateProfile?.resume_filename;
  const selectedInterviewDetails = selectedApplicant?.interview_details;

  const openInterviewModal = (app = selectedApplicant) => {
    if (!app) return;
    setInterviewForm({
      date: app.interview_details?.date || '',
      startTime: app.interview_details?.start_time || '',
      timezone: app.interview_details?.timezone || 'Asia/Kolkata',
      meetingLink: app.interview_details?.meeting_link || '',
      meetingPlatform: app.interview_details?.meeting_platform || 'Google Meet',
      panel: (app.interview_details?.panel || []).join(', '),
      notes: app.interview_details?.notes || '',
      location: app.interview_details?.location || '',
    });
    setInterviewError('');
    setInterviewModalOpen(true);
  };

  const closeInterviewModal = () => {
    setInterviewModalOpen(false);
    setInterviewForm(initialInterviewForm);
    setInterviewError('');
  };

  const handleInterviewFormChange = (field, value) => {
    setInterviewForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveInterview = async (event) => {
    event.preventDefault();
    if (!selectedApplicant) return;
    if (!interviewForm.date || !interviewForm.startTime || !interviewForm.meetingLink) {
      setInterviewError('Please provide date, time, and meeting link.');
      return;
    }
    setInterviewSaving(true);
    setInterviewError('');
    try {
      const payload = {
        date: interviewForm.date,
        start_time: interviewForm.startTime,
        timezone: interviewForm.timezone,
        meeting_link: interviewForm.meetingLink,
        meeting_platform: interviewForm.meetingPlatform,
        panel: interviewForm.panel
          ? interviewForm.panel.split(',').map((item) => item.trim()).filter(Boolean)
          : [],
        notes: interviewForm.notes || null,
        location: interviewForm.location || null,
      };
      const updatedApp = await updateInterviewDetails(selectedApplicant.id, payload);
      if (updatedApp) {
        setApplications(prev =>
          prev.map(app => (app.id === updatedApp.id ? updatedApp : app)),
        );
        setSelectedApplicant(updatedApp);
        addActivity(
          `Interview scheduled for ${updatedApp.candidate_name}`,
          'Interview',
          updatedApp,
        );
        addNotification(
          `Interview scheduled with ${updatedApp.candidate_name}`,
          'event',
        );
      }
      closeInterviewModal();
      handleSendInviteMail(updatedApp);
    } catch (error) {
      setInterviewError(error.message || 'Failed to save interview details.');
    } finally {
      setInterviewSaving(false);
    }
  };

  const handleSendInviteMail = (application = selectedApplicant) => {
    if (!application?.candidate_email) return;
    if (!application?.interview_details?.meeting_link) {
      alert('Please add the meeting link before sending an invite.');
      return;
    }
    const details = application.interview_details;
    const scheduleText = details ? formatInterviewSlot(details) : 'To be decided';
    const linkLine = details?.meeting_link ? `Meeting link: ${details.meeting_link}` : '';
    const panelLine =
      details?.panel && details.panel.length
        ? `Panel: ${details.panel.join(', ')}`
        : '';
    const subject = `Interview Schedule - ${application.job_title || 'Your application'}`;
    const body = `Hi ${profileName},

Your interview for ${application.job_title || 'the role'} at ${hrCompanyName} is scheduled on ${scheduleText}.
${linkLine}
${panelLine}

Please reply to confirm or reschedule if needed.

Thanks,
${hrCompanyName} hiring team`;
    window.location.href = `mailto:${application.candidate_email}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
  };

  const handleCopyMeetingLink = async (application = selectedApplicant) => {
    const link = application?.interview_details?.meeting_link;
    if (!link) {
      alert('No meeting link available. Please schedule the interview first.');
      return;
    }
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = link;
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      addNotification('Meeting link copied to clipboard', 'content_copy');
    } catch (error) {
      console.error('Copy failed:', error);
    }
  };

  const companyProfileData = {
    ...companyProfile,
    name: hrCompanyName || COMPANY_CONFIG.name,
  };

  return (
    <HrDashboardLayout>
      {/* Floating Notification Bell for Messages */}
      <button
        onClick={() => {
          // Show messaging section or open first unread conversation
          const appsWithUnread = getApplicationsWithUnreadMessages();
          if (appsWithUnread.length > 0) {
            const firstUnread = applications.find(app => app.id === appsWithUnread[0].applicationId);
            if (firstUnread) {
              setSelectedApplicant(firstUnread);
              handleOpenChat({
                candidate: firstUnread.candidate_name,
                candidateEmail: firstUnread.candidate_email,
                role: firstUnread.job_title,
                applicationId: firstUnread.id,
              });
            }
          }
        }}
        className="fixed bottom-6 right-6 z-40 p-4 rounded-full bg-teal-600 text-white shadow-lg hover:shadow-xl transition-all hover:scale-110"
        title={`${hrUnreadCount} unread message${hrUnreadCount !== 1 ? 's' : ''}`}
      >
        <Icon name="chat" size={24} />
        {hrUnreadCount > 0 && (
          <span className="absolute -top-1 -right-1 flex h-6 w-6 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white animate-pulse">
            {hrUnreadCount}
          </span>
        )}
      </button>
      
      <main className="flex-1 px-4 py-6 sm:px-6 lg:px-10 space-y-10">
        {/* Summary */}
        <section className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
          {summaryStats.map((stat) => (
            <div key={stat.label} className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <div className={`w-12 h-12 rounded-lg ${stat.iconBg} flex items-center justify-center`}>
                  <Icon name={stat.icon} size={24} className={stat.iconColor} />
                </div>
                <span className="text-2xl font-bold text-gray-900">{stat.value}</span>
              </div>
              <h3 className="text-sm font-medium text-gray-600">{stat.label}</h3>
              <p className="text-xs text-gray-500 mt-1">{stat.trend}</p>
            </div>
          ))}
        </section>

        {/* Interview Assessments Quick Access */}
        <section className="rounded-xl bg-gradient-to-r from-teal-600 to-teal-700 p-6 shadow-sm text-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold mb-2">Interview Assessments</h2>
              <p className="text-teal-100 mb-4">
                Review AI-powered interview reports with video recordings, transcripts, and performance scores
              </p>
              <div className="flex gap-4 text-sm">
                <div className="flex items-center gap-2">
                  <Icon name="videocam" size={20} />
                  <span>Video Recordings</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="description" size={20} />
                  <span>Transcripts</span>
                </div>
                <div className="flex items-center gap-2">
                  <Icon name="analytics" size={20} />
                  <span>AI Scoring</span>
                </div>
              </div>
            </div>
            <Button
              size="lg"
              variant="secondary"
              onClick={() => navigate('/hr/interviews')}
              className="bg-white text-teal-600 hover:bg-teal-50"
            >
              <Icon name="assessment" size={20} className="mr-2" />
              View All Interviews
            </Button>
          </div>
        </section>

        {/* Job management & activity */}
        <section id="jobs" className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200 lg:col-span-2">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-xl font-bold">Job Management</h2>
                <p className="text-xs text-gray-500 mt-1">{hrCompanyName} · IT & Non-IT mandates</p>
              </div>
              <Button
                size="sm"
                variant={jobFormOpen ? 'outline' : 'primary'}
                onClick={() => (jobFormOpen ? closeJobForm() : setJobFormOpen(true))}
              >
                {jobFormOpen ? 'Close Form' : 'Create Job Post'}
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 text-xs mb-4">
              {['All', 'Active', 'Closed'].map((status) => (
                <button
                  key={status}
                  type="button"
                  onClick={() => setJobStatusFilter(status)}
                  className={`rounded-full border px-4 py-1 font-semibold ${
                    jobStatusFilter === status
                      ? 'border-teal-500 text-teal-600 bg-teal-50'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {status}
                </button>
              ))}
            </div>
            <div className="flex flex-wrap gap-2 text-xs mb-4">
              {['All', 'IT', 'Non-IT'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setJobTypeFilter(type)}
                  className={`rounded-full border px-4 py-1 font-semibold ${
                    jobTypeFilter === type
                      ? 'border-teal-500 text-teal-600 bg-teal-50'
                      : 'border-gray-200 text-gray-600'
                  }`}
                >
                  {type}
                </button>
              ))}
            </div>
            {jobFormOpen && (
              <div className="rounded-2xl border border-dashed border-gray-200 bg-gray-50/60 p-4/40 mb-4">
                <form className="space-y-4" onSubmit={handleCreateJob}>
                  <div className="grid gap-4 md:grid-cols-2">
                    <Input
                      placeholder="Job title"
                      value={jobForm.title}
                      onChange={(event) => handleJobFormChange('title', event.target.value)}
                    />
                    <Input
                      placeholder="Location (e.g., Bengaluru, India)"
                      value={jobForm.location}
                      onChange={(event) => handleJobFormChange('location', event.target.value)}
                    />
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <select
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      value={jobForm.employmentType}
                      onChange={(event) => handleJobFormChange('employmentType', event.target.value)}
                    >
                      <option value="Full-time">Full-time</option>
                      <option value="Contract">Contract</option>
                      <option value="Internship">Internship</option>
                    </select>
                    <select
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      value={jobForm.workType}
                      onChange={(event) => handleJobFormChange('workType', event.target.value)}
                    >
                      <option value="on-site">On-site</option>
                      <option value="hybrid">Hybrid</option>
                      <option value="remote">Remote</option>
                    </select>
                    <select
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      value={jobForm.experienceLevel}
                      onChange={(event) => handleJobFormChange('experienceLevel', event.target.value)}
                    >
                      <option value="entry">Entry</option>
                      <option value="mid">Mid</option>
                      <option value="senior">Senior</option>
                    </select>
                  </div>
                  <div className="grid gap-4 md:grid-cols-3">
                    <Input
                      placeholder="Department / Team"
                      value={jobForm.department}
                      onChange={(event) => handleJobFormChange('department', event.target.value)}
                    />
                    <select
                      className="rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      value={jobForm.category}
                      onChange={(event) => handleJobFormChange('category', event.target.value)}
                    >
                      <option value="IT">IT</option>
                      <option value="Non-IT">Non-IT</option>
                    </select>
                    <Input
                      placeholder="Tags (comma separated)"
                      value={jobForm.tags}
                      onChange={(event) => handleJobFormChange('tags', event.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Description (Text) *
                    </label>
                    <textarea
                      className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                      rows={4}
                      placeholder="Role description, responsibilities, skills..."
                      value={jobForm.description}
                      onChange={(event) => handleJobFormChange('description', event.target.value)}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Job Description PDF (Optional)
                      <span className="text-xs text-gray-500 ml-2">- ATS will compare Resume PDF with JD PDF</span>
                    </label>
                    <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                      <input
                        type="file"
                        accept=".pdf,application/pdf"
                        onChange={(event) => {
                          const file = event.target.files?.[0];
                          if (file) {
                            if (file.size > 5 * 1024 * 1024) {
                              setJobFormError('JD PDF file size must be less than 5MB.');
                              event.target.value = '';
                              return;
                            }
                            if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                              setJobFormError('Please upload a PDF file only.');
                              event.target.value = '';
                              return;
                            }
                            handleJobFormChange('jdPdfFile', file);
                            setJobFormError('');
                          }
                        }}
                        className="w-full text-sm text-gray-600"
                      />
                      {jobForm.jdPdfFile && (
                        <p className="mt-2 text-sm text-green-600">
                          <Icon name="check_circle" size={16} className="inline mr-1" />
                          {jobForm.jdPdfFile.name} selected
                        </p>
                      )}
                      <p className="mt-1 text-xs text-gray-500">
                        Upload JD as PDF for better ATS scoring. If both text and PDF are provided, PDF will be used for ATS calculation.
                      </p>
                    </div>
                  </div>

                  {/* Interview Configuration (Optional) */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="quiz" size={20} className="text-teal-600" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Interview Configuration (Optional)
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Customize the AI-generated interview questions for this role
                    </p>

                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Total Questions
                        </label>
                        <Input
                          type="number"
                          min="3"
                          max="15"
                          value={jobForm.totalQuestions}
                          onChange={(e) => handleJobFormChange('totalQuestions', parseInt(e.target.value) || 5)}
                          placeholder="5"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Technical Questions
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max={jobForm.totalQuestions}
                          value={jobForm.technicalQuestions}
                          onChange={(e) => handleJobFormChange('technicalQuestions', parseInt(e.target.value) || 0)}
                          placeholder="3"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Behavioral Questions
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max={jobForm.totalQuestions}
                          value={jobForm.behavioralQuestions}
                          onChange={(e) => handleJobFormChange('behavioralQuestions', parseInt(e.target.value) || 0)}
                          placeholder="1"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Situational Questions
                        </label>
                        <Input
                          type="number"
                          min="0"
                          max={jobForm.totalQuestions}
                          value={jobForm.situationalQuestions}
                          onChange={(e) => handleJobFormChange('situationalQuestions', parseInt(e.target.value) || 0)}
                          placeholder="1"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Additional Requirements / Custom Prompt
                        <span className="text-xs text-gray-500 ml-2">- Guide AI question generation</span>
                      </label>
                      <textarea
                        value={jobForm.additionalRequirements}
                        onChange={(e) => handleJobFormChange('additionalRequirements', e.target.value)}
                        placeholder="E.g., Focus on cloud architecture experience, ask about microservices, candidate should have strong communication skills..."
                        rows="4"
                        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                      <p className="mt-1 text-xs text-gray-500">
                        This will be used by AI to generate relevant interview questions and for resume matching (ATS scoring)
                      </p>
                    </div>
                  </div>

                  {/* Auto-Rejection Configuration */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="auto_awesome" size={20} className="text-orange-500" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Auto-Rejection Settings
                      </h3>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Automatically reject candidates who score below the threshold in AI assessment interviews
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="autoRejectEnabled"
                          checked={jobForm.autoRejectEnabled}
                          onChange={(e) => handleJobFormChange('autoRejectEnabled', e.target.checked)}
                          className="w-4 h-4 text-teal-600 bg-gray-100 border-gray-300 rounded focus:ring-teal-500 focus:ring-2"
                        />
                        <label htmlFor="autoRejectEnabled" className="text-sm font-medium text-gray-700">
                          Enable automatic rejection based on interview assessment scores
                        </label>
                      </div>

                      {jobForm.autoRejectEnabled && (
                        <div className="ml-7 space-y-4 p-4 bg-orange-50 rounded-lg border border-orange-200">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Minimum Score Threshold (%)
                            </label>
                            <div className="flex items-center gap-4">
                              <Input
                                type="number"
                                min="0"
                                max="100"
                                value={jobForm.autoRejectThreshold}
                                onChange={(e) => handleJobFormChange('autoRejectThreshold', parseInt(e.target.value) || 70)}
                                placeholder="70"
                                className="w-24"
                              />
                              <span className="text-sm text-gray-600">
                                Candidates scoring below {jobForm.autoRejectThreshold}% will be automatically rejected
                              </span>
                            </div>
                          </div>
                          
                          <div className="bg-white p-3 rounded border border-orange-300">
                            <div className="flex items-start gap-2">
                              <Icon name="info" size={16} className="text-orange-600 mt-0.5" />
                              <div className="text-xs text-gray-600">
                                <p className="font-medium mb-1">How it works:</p>
                                <ul className="space-y-1">
                                  <li>• Candidates complete AI assessment interviews</li>
                                  <li>• Those scoring below {jobForm.autoRejectThreshold}% are automatically rejected</li>
                                  <li>• Rejection emails with AI-generated feedback are sent automatically</li>
                                  <li>• Only candidates above the threshold appear in your applicant tracking</li>
                                  <li>• You'll see the count of auto-rejected candidates in the job card</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Live Interview Configuration */}
                  <div className="border-t border-gray-200 pt-6">
                    <div className="flex items-center gap-2 mb-4">
                      <Icon name="record_voice_over" size={20} className="text-blue-500" />
                      <h3 className="text-lg font-semibold text-gray-900">
                        Live AI Interview Settings
                      </h3>
                      <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full font-medium">
                        PREMIUM
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mb-4">
                      Enable real-time voice interviews with AI for this position. Candidates will have live conversations with AI interviewer.
                    </p>

                    <div className="space-y-4">
                      <div className="flex items-center gap-3">
                        <input
                          type="checkbox"
                          id="liveInterviewEnabled"
                          checked={jobForm.liveInterviewEnabled}
                          onChange={(e) => handleJobFormChange('liveInterviewEnabled', e.target.checked)}
                          className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                        />
                        <label htmlFor="liveInterviewEnabled" className="text-sm font-medium text-gray-700">
                          Enable Live AI Interview for this job
                        </label>
                      </div>

                      {jobForm.liveInterviewEnabled && (
                        <div className="ml-7 space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Interview Duration (minutes)
                              </label>
                              <select
                                value={jobForm.liveInterviewDuration}
                                onChange={(e) => handleJobFormChange('liveInterviewDuration', parseInt(e.target.value))}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500"
                              >
                                <option value={3}>3 minutes (Quick)</option>
                                <option value={5}>5 minutes (Standard)</option>
                                <option value={10}>10 minutes (Detailed)</option>
                                <option value={15}>15 minutes (Comprehensive)</option>
                              </select>
                            </div>
                            
                            <div>
                              <label className="block text-sm font-medium text-gray-700 mb-2">
                                Interview Language
                              </label>
                              <select
                                value={jobForm.liveInterviewLanguage}
                                onChange={(e) => handleJobFormChange('liveInterviewLanguage', e.target.value)}
                                className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:ring-2 focus:ring-blue-500"
                              >
                                <option value="en-US">English (US)</option>
                                <option value="en-IN">English (India)</option>
                                <option value="hi-IN">Hindi</option>
                                <option value="ta-IN">Tamil</option>
                                <option value="es-US">Spanish</option>
                                <option value="fr-FR">French</option>
                                <option value="de-DE">German</option>
                                <option value="ja-JP">Japanese</option>
                                <option value="ko-KR">Korean</option>
                                <option value="cmn-CN">Chinese (Mandarin)</option>
                              </select>
                            </div>
                          </div>

                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Interview Focus Areas
                            </label>
                            <div className="grid grid-cols-2 gap-3">
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={jobForm.liveInterviewFocus?.includes('technical')}
                                  onChange={(e) => {
                                    const focus = jobForm.liveInterviewFocus || [];
                                    if (e.target.checked) {
                                      handleJobFormChange('liveInterviewFocus', [...focus, 'technical']);
                                    } else {
                                      handleJobFormChange('liveInterviewFocus', focus.filter(f => f !== 'technical'));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700">Technical Skills</span>
                              </label>
                              
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={jobForm.liveInterviewFocus?.includes('behavioral')}
                                  onChange={(e) => {
                                    const focus = jobForm.liveInterviewFocus || [];
                                    if (e.target.checked) {
                                      handleJobFormChange('liveInterviewFocus', [...focus, 'behavioral']);
                                    } else {
                                      handleJobFormChange('liveInterviewFocus', focus.filter(f => f !== 'behavioral'));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700">Behavioral</span>
                              </label>
                              
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={jobForm.liveInterviewFocus?.includes('experience')}
                                  onChange={(e) => {
                                    const focus = jobForm.liveInterviewFocus || [];
                                    if (e.target.checked) {
                                      handleJobFormChange('liveInterviewFocus', [...focus, 'experience']);
                                    } else {
                                      handleJobFormChange('liveInterviewFocus', focus.filter(f => f !== 'experience'));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700">Experience</span>
                              </label>
                              
                              <label className="flex items-center gap-2">
                                <input
                                  type="checkbox"
                                  checked={jobForm.liveInterviewFocus?.includes('cultural')}
                                  onChange={(e) => {
                                    const focus = jobForm.liveInterviewFocus || [];
                                    if (e.target.checked) {
                                      handleJobFormChange('liveInterviewFocus', [...focus, 'cultural']);
                                    } else {
                                      handleJobFormChange('liveInterviewFocus', focus.filter(f => f !== 'cultural'));
                                    }
                                  }}
                                  className="w-4 h-4 text-blue-600 rounded"
                                />
                                <span className="text-sm text-gray-700">Cultural Fit</span>
                              </label>
                            </div>
                          </div>
                          
                          <div className="bg-white p-3 rounded border border-blue-300">
                            <div className="flex items-start gap-2">
                              <Icon name="info" size={16} className="text-blue-600 mt-0.5" />
                              <div className="text-xs text-gray-600">
                                <p className="font-medium mb-1">Live Interview Features:</p>
                                <ul className="space-y-1">
                                  <li>• Real-time voice conversation with AI interviewer</li>
                                  <li>• Adaptive questions based on candidate responses</li>
                                  <li>• Automatic scoring and detailed feedback</li>
                                  <li>• Multi-language support for global candidates</li>
                                  <li>• Professional interview experience with instant results</li>
                                </ul>
                              </div>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {jobFormError && <p className="text-sm text-red-500">{jobFormError}</p>}
                  <div className="flex flex-wrap gap-3">
                    <Button type="submit" size="sm" disabled={jobSubmitting}>
                      {jobSubmitting ? 'Publishing...' : 'Publish Role'}
                    </Button>
                    <Button type="button" size="sm" variant="outline" onClick={closeJobForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </div>
            )}
            <div className="space-y-4">
              {jobsLoading && (
                <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-sm text-gray-500">
                  Loading your open roles…
                </div>
              )}
              {!jobsLoading && filteredJobs.length === 0 && (
                <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-sm text-gray-500">
                  No {jobTypeFilter === 'All' ? '' : jobTypeFilter} roles found under "{jobStatusFilter}". Try another filter or create a new job post.
                </div>
              )}
              {!jobsLoading &&
                filteredJobs.map((job) => (
                  <div
                    key={job.id || job.title}
                    className="flex flex-col gap-3 border border-gray-100 rounded-2xl px-4 py-3 md:flex-row md:items-center md:justify-between"
                  >
                    <div>
                      <p className="font-semibold text-gray-900">{job.title}</p>
                      <p className="text-xs text-gray-500">
                        {job.status} · {job.views} views · {job.applicants} applicants
                        {job.auto_rejected_count > 0 && (
                          <span className="ml-2 text-orange-600">
                            · {job.auto_rejected_count} auto-rejected
                          </span>
                        )}
                      </p>
                      <p className="text-xs text-gray-400">
                        {job.department} · {job.location} · {job.jobType} · {job.experience}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2 text-sm">
                      <Button 
                        size="sm" 
                        variant="secondary"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEditJobModal(job);
                        }}
                      >
                        View & Edit
                      </Button>
                      <Button
                        size="sm"
                        variant={job.status === 'Active' ? 'outline' : 'secondary'}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleJobStatusToggle(job);
                        }}
                        className={job.status === 'Active' ? '' : 'bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100'}
                      >
                        {job.status === 'Active' ? 'Close' : 'Active'}
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDeleteJob(job);
                        }}
                        className="text-red-600 border-red-300 hover:bg-red-50 hover:border-red-400"
                      >
                        <Icon name="delete" size={16} />
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
            </div>
          </div>
          <div className="space-y-4">
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-4">Notifications</h2>
              {loadingDashboard ? (
                <div className="text-sm text-gray-500">Loading notifications...</div>
              ) : notifications.length > 0 ? (
                <ul className="space-y-3 text-sm text-gray-700">
                  {notifications.slice(0, 10).map((notification) => (
                    <li key={notification.id} className="flex items-start gap-3 p-2 rounded-lg hover:bg-gray-50 transition-colors">
                      <Icon name={notification.icon || 'notifications'} className="text-teal-600 flex-shrink-0 mt-0.5" />
                      <div className="flex-1">
                        <span>{notification.message}</span>
                        {notification.time && (
                          <p className="text-xs text-gray-500 mt-1">
                            {formatTimeAgo(notification.time)}
                          </p>
                        )}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-sm text-gray-500">No new notifications</div>
              )}
            </div>
            <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
              <h2 className="text-xl font-bold mb-4">Recent Activity</h2>
              <ul className="space-y-3 text-sm">
                {recentActivity.map((item) => (
                  <li key={item.title} className="flex items-start justify-between gap-4 border-b border-gray-100 pb-3 last:border-none last:pb-0">
                    <div>
                      <p className="font-semibold">{item.title}</p>
                      <p className="text-xs text-gray-500">{item.type}</p>
                    </div>
                    <span className="text-xs text-gray-400 whitespace-nowrap">{item.time}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        {/* Applicant tracking + profile viewer */}
        <section className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200 lg:col-span-2">
            <div className="flex flex-wrap gap-3 items-center mb-4">
              <h2 className="text-xl font-bold">Applicant Tracking</h2>
              <div className="flex flex-wrap gap-2 text-xs text-gray-500">
                {['Experience', 'Skills', 'ATS Score', 'Location'].map((filter) => (
                  <span key={filter} className="rounded-full border border-gray-200 px-3 py-1">
                    {filter}
                  </span>
                ))}
              </div>
              {/* Job Filter Dropdown */}
              <div className="ml-auto">
                <select
                  className="text-xs rounded-full border border-gray-200 px-3 py-1 bg-white"
                  value={selectedJobFilter || 'all'}
                  onChange={(e) => setSelectedJobFilter(e.target.value === 'all' ? null : e.target.value)}
                >
                  <option value="all">All Jobs</option>
                  {companyJobs.map((job) => (
                    <option key={job.id} value={job.id}>
                      {job.title}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            {/* Dynamic Pipeline Stats */}
            <div className="grid gap-3 md:grid-cols-5 mb-6">
              {pipelineStats.map(({ stage, status, count }) => {
                const percentage = totalPipelineCount > 0 
                  ? Math.max((count / totalPipelineCount) * 100, count > 0 ? 6 : 0)
                  : 0;
                const isRejected = status === 'rejected';
                const isOffer = status === 'offer';
                
                return (
                  <div 
                    key={stage} 
                    className={`rounded-2xl border p-3 transition-all ${
                      isRejected 
                        ? 'border-red-200 bg-red-50' 
                        : isOffer
                        ? 'border-green-200 bg-green-50'
                        : 'border-gray-100 bg-gray-50'
                    }`}
                  >
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                      {stage}
                    </p>
                    <p className={`text-2xl font-bold mt-1 ${
                      isRejected 
                        ? 'text-red-600' 
                        : isOffer
                        ? 'text-green-600'
                        : 'text-gray-900'
                    }`}>
                      {count}
                    </p>
                    <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                      <span
                        className={`block h-full transition-all ${
                          isRejected 
                            ? 'bg-red-500' 
                            : isOffer
                            ? 'bg-green-500'
                            : 'bg-teal-600'
                        }`}
                        style={{ width: `${percentage}%` }}
                      />
                    </div>
                    {count > 0 && (
                      <p className="text-[10px] text-gray-500 mt-1">
                        {percentage.toFixed(0)}% of total
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Additional Dynamic Metrics */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500 mb-1">Total Applications</p>
                <p className="text-xl font-bold text-gray-900">{totalApplications}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500 mb-1">Active</p>
                <p className="text-xl font-bold text-emerald-600">{activeApplications}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500 mb-1">Rejected</p>
                <p className="text-xl font-bold text-red-600">{rejectedCount}</p>
              </div>
              <div className="rounded-xl border border-gray-200 bg-white p-3">
                <p className="text-xs text-gray-500 mb-1">Avg ATS Score</p>
                <p className="text-xl font-bold text-teal-600">{averageATSScore}%</p>
              </div>
            </div>
            <div className="overflow-x-auto">
              {loadingApplicants ? (
                <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-sm text-gray-500 text-center">
                  Loading applications...
                </div>
              ) : applications.length === 0 ? (
                <div className="rounded-2xl border border-dashed border-gray-300 p-6 text-sm text-gray-500 text-center">
                  No applications found. Applications will appear here when candidates apply to your job posts.
                </div>
              ) : (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="pb-2">Candidate</th>
                      <th className="pb-2">Job</th>
                      <th className="pb-2">ATS</th>
                      <th className="pb-2">Assessment</th>
                      <th className="pb-2">Stage</th>
                      <th className="pb-2">Applied</th>
                      <th className="pb-2">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {applications
                      .filter((app) => {
                        // Filter out auto-rejected candidates (they should not appear in tracking)
                        if (app.status === 'rejected' && app.auto_rejected) {
                          return false;
                        }
                        
                        // Filter by selected job if specified
                        if (selectedJobFilter && app.job_id !== selectedJobFilter) {
                          return false;
                        }
                        
                        // Only show assessment completed candidates above threshold
                        if (app.status === 'interview_completed' || app.status === 'offer') {
                          const interviewScore = app.interview_details?.interview_score || 
                                               app.interview_details?.scoring_result?.overall_percentage;
                          
                          // If there's an interview score, check if it meets threshold
                          if (interviewScore !== undefined) {
                            // Get job threshold (default 70 if not set)
                            const job = companyJobs.find(j => j.id === app.job_id);
                            const threshold = job?.auto_reject_threshold || 70;
                            
                            // Only show if score is above threshold
                            return interviewScore >= threshold;
                          }
                        }
                        
                        return true;
                      })
                      .map((app) => (
                      <tr
                        key={app.id}
                        className={`border-b ${selectedApplicant?.id === app.id ? 'bg-teal-50' : ''}`}
                      >
                        <td 
                          className="py-3 font-semibold cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          {app.candidate_name}
                        </td>
                        <td 
                          className="py-3 cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          {app.job_title || app.job_id}
                        </td>
                        <td 
                          className="py-3 cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          <span className={`font-semibold ${
                            (app.ats_score || 0) >= 70 ? 'text-green-600' :
                            (app.ats_score || 0) >= 50 ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {app.ats_score ? `${app.ats_score.toFixed(1)}%` : '—'}
                          </span>
                        </td>
                        <td 
                          className="py-3 cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          {/* Try multiple ways to get the interview score */}
                          {(() => {
                            // Debug: Log the interview_details structure
                            if (app.interview_details && app.candidate_name === 'Ashwini Dhanasekaran') {
                              console.log('Interview details for Ashwini:', app.interview_details);
                              console.log('Type:', typeof app.interview_details);
                              console.log('Keys:', Object.keys(app.interview_details || {}));
                            }
                            
                            // Method 1: Direct interview_score in interview_details
                            let score = app.interview_details?.interview_score;
                            
                            // Method 2: From scoring_result in interview_details
                            if (!score && app.interview_details?.scoring_result) {
                              score = app.interview_details.scoring_result.overall_percentage;
                            }
                            
                            // Method 3: Check if interview_details is a string that needs parsing
                            if (!score && typeof app.interview_details === 'string') {
                              try {
                                const parsed = JSON.parse(app.interview_details);
                                score = parsed.interview_score || parsed.scoring_result?.overall_percentage;
                              } catch (e) {
                                console.log('Failed to parse interview_details:', e);
                              }
                            }
                            
                            if (score && typeof score === 'number') {
                              return (
                                <span className={`font-semibold ${
                                  score >= 70 ? 'text-green-600' :
                                  score >= 50 ? 'text-yellow-600' :
                                  'text-red-600'
                                }`}>
                                  {score.toFixed(0)}%
                                </span>
                              );
                            } else {
                              return <span className="text-gray-400">—</span>;
                            }
                          })()}
                        </td>
                        <td 
                          className="py-3 cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            app.status === 'applied' 
                              ? 'bg-blue-100 text-blue-700'
                              : app.status === 'review'
                              ? 'bg-amber-100 text-amber-700'
                              : app.status === 'interview'
                              ? 'bg-purple-100 text-purple-700'
                              : app.status === 'interview_completed'
                              ? 'bg-indigo-100 text-indigo-700'
                              : app.status === 'offer'
                              ? 'bg-green-100 text-green-700'
                              : app.status === 'rejected'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-gray-100 text-gray-700'
                          }`}>
                            {app.status === 'applied' ? 'Applied' :
                             app.status === 'review' ? 'Under Review' :
                             app.status === 'interview' ? 'Interview' :
                             app.status === 'interview_completed' ? 'Completed' :
                             app.status === 'offer' ? 'Offer' :
                             app.status === 'rejected' ? 'Rejected' :
                             app.status || 'Unknown'}
                          </span>
                        </td>
                        <td 
                          className="py-3 text-gray-500 cursor-pointer"
                          onClick={() => setSelectedApplicant(app)}
                        >
                          {new Date(app.applied_at).toLocaleDateString()}
                        </td>
                        <td className="py-3">
                          <div className="flex gap-1 items-center flex-wrap" onClick={(e) => e.stopPropagation()}>
                            
                            {/* APPLIED status: Review, Accept, Reject */}
                            {app.status === 'applied' && (
                              <>
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                                  onClick={() => setSelectedApplicant(app)}
                                  title="View Candidate Details"
                                >
                                  <Icon name="visibility" size={14} />
                                  <span>Review</span>
                                </button>
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-green-300 bg-white text-green-600 hover:bg-green-50 transition-colors"
                                  onClick={() => handleMoveToOffer(app.id)}
                                  title="Accept & Send Offer"
                                >
                                  <Icon name="check_circle" size={14} />
                                  <span>Accept</span>
                                </button>
                              </>
                            )}
                            
                            {/* REVIEW status: Interview, Accept, Reject */}
                            {app.status === 'review' && (
                              <>
                                {/* Check if job has live interview enabled */}
                                {companyJobs.find(job => job.id === app.job_id)?.live_interview_enabled ? (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors"
                                    onClick={() => navigate(`/live-interview/${app.id}`)}
                                    title="Start Live AI Interview"
                                  >
                                    <Icon name="record_voice_over" size={14} />
                                    <span>Live Interview</span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-purple-300 bg-white text-purple-600 hover:bg-purple-50 transition-colors"
                                    onClick={() => handleMoveToInterview(app.id)}
                                    title="Schedule Interview"
                                  >
                                    <Icon name="calendar_today" size={14} />
                                    <span>Interview</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-green-300 bg-white text-green-600 hover:bg-green-50 transition-colors"
                                  onClick={() => handleMoveToOffer(app.id)}
                                  title="Accept & Send Offer"
                                >
                                  <Icon name="check_circle" size={14} />
                                  <span>Accept</span>
                                </button>
                              </>
                            )}
                            
                            {/* INTERVIEW status: Review (report if available), Accept, Reject */}
                            {app.status === 'interview' && (
                              <>
                                {app.interview_details?.interview_id ? (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                                    onClick={() => navigate(`/hr/interview-review/${app.interview_details.interview_id}`)}
                                    title="View Assessment Report"
                                  >
                                    <Icon name="assessment" size={14} />
                                    <span>Review</span>
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                                    onClick={() => setSelectedApplicant(app)}
                                    title="View Candidate Details"
                                  >
                                    <Icon name="visibility" size={14} />
                                    <span>Review</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-green-300 bg-white text-green-600 hover:bg-green-50 transition-colors"
                                  onClick={() => handleMoveToOffer(app.id)}
                                  title="Accept & Send Offer"
                                >
                                  <Icon name="check_circle" size={14} />
                                  <span>Accept</span>
                                </button>
                              </>
                            )}
                            
                            {/* INTERVIEW_COMPLETED status: Review (report), Accept, Reject */}
                            {app.status === 'interview_completed' && (
                              <>
                                {app.interview_details?.interview_id && (
                                  <button
                                    type="button"
                                    className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-blue-300 bg-white text-blue-600 hover:bg-blue-50 transition-colors"
                                    onClick={() => {
                                      // Check if this is a live interview (session ID starts with 'session-' or is an application ID)
                                      const interviewId = app.interview_details.interview_id;
                                      if (interviewId.startsWith('app-') || interviewId.startsWith('session-')) {
                                        // This is a live interview - navigate to live interview report page
                                        navigate(`/hr/live-interview-report/${interviewId}`);
                                      } else {
                                        // Regular interview - use existing route
                                        navigate(`/hr/interview-review/${interviewId}`);
                                      }
                                    }}
                                    title="View Assessment Report"
                                  >
                                    <Icon name="assessment" size={14} />
                                    <span>Review</span>
                                  </button>
                                )}
                                <button
                                  type="button"
                                  className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-green-300 bg-white text-green-600 hover:bg-green-50 transition-colors"
                                  onClick={() => handleMoveToOffer(app.id)}
                                  title="Accept & Send Offer"
                                >
                                  <Icon name="check_circle" size={14} />
                                  <span>Accept</span>
                                </button>
                              </>
                            )}
                            
                            {/* OFFER status: Show badge */}
                            {app.status === 'offer' && (
                              <span className="px-2 py-1 text-xs font-medium rounded-lg bg-green-100 text-green-700">
                                Offer Sent
                              </span>
                            )}
                            
                            {/* Reject button - available for all except rejected and offer */}
                            {app.status !== 'rejected' && app.status !== 'offer' && (
                              <button
                                type="button"
                                className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-red-300 bg-white text-red-600 hover:bg-red-50 transition-colors"
                                onClick={() => handleRejectApplication(app.id)}
                                title="Reject Application"
                              >
                                <Icon name="close" size={14} />
                                <span>Reject</span>
                              </button>
                            )}
                            
                            {/* Delete button */}
                            <button
                              type="button"
                              className="flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 transition-colors"
                              onClick={() => handleDeleteApplication(app)}
                              title="Delete Application"
                            >
                              <Icon name="delete" size={14} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Candidate Profile</h2>
            {selectedApplicant ? (
              <div className="space-y-4 text-sm">
                <div>
                  <p className="text-lg font-semibold">{profileName}</p>
                  <p className="text-gray-500">{profileEmail}</p>
                  {profileRole && (
                    <p className="text-xs text-gray-400 mt-0.5">{profileRole}</p>
                  )}
                </div>
                {candidateProfileLoading && (
                  <p className="text-xs text-gray-500">Loading candidate profile…</p>
                )}
                {candidateProfileError && (
                  <p className="text-xs text-red-500">{candidateProfileError}</p>
                )}
                <div className="rounded-2xl border border-gray-100 p-3 bg-gray-50">
                  <p className="text-xs text-gray-500 mb-1">ATS Score</p>
                  <p className="text-3xl font-bold text-teal-600">{selectedApplicant.ats_score ?? '—'}%</p>
                  <p className="text-xs text-gray-500 mt-1">Resume match rating</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1 uppercase tracking-wide">Skills focus</p>
                  <div className="flex flex-wrap gap-2">
                    {candidateSkills.map((skill) => (
                      <span key={skill} className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-600">
                        {skill}
                      </span>
                    ))}
                  </div>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  <div className="rounded-2xl border border-gray-100 p-3">
                    <p className="text-xs text-gray-500 mb-1">Experience / Role</p>
                    <p className="font-semibold text-gray-900">
                      {selectedApplicant.experience_summary || profileRole || 'Experience not provided'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {selectedApplicant.job_title || selectedApplicant.job_id || hrCompanyName}
                    </p>
                  </div>
                  <div className="rounded-2xl border border-gray-100 p-3">
                    <p className="text-xs text-gray-500 mb-1">Contact</p>
                    <p className="font-semibold text-gray-900">
                      {profilePhone}
                    </p>
                    <p className="text-xs text-gray-500 break-all">
                      {profileEmail}
                    </p>
                  </div>
                </div>
                <div className="rounded-2xl border border-dashed border-gray-300 p-3 bg-white">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Resume Preview</p>
                    {resumeUrl ? (
                      <a
                        href={resumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        download
                      >
                        <Button size="sm" variant="secondary">
                          <Icon name="download" size={16} className="mr-1" />
                          {resumeFilename ? `Download ${resumeFilename}` : 'View/Download PDF'}
                        </Button>
                      </a>
                    ) : (
                      <Button size="sm" variant="secondary" disabled>
                        No Resume Available
                      </Button>
                    )}
                  </div>
                  <p className="text-sm text-gray-700 line-clamp-5">
                    {resumeSummary}
                  </p>
                </div>
                <div className="rounded-2xl border border-gray-100 p-3 bg-gray-50/40">
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-gray-500 uppercase tracking-wide">Interview Details</p>
                    <Button size="sm" variant="outline" onClick={() => openInterviewModal()} disabled={!selectedApplicant}>
                      Edit
                    </Button>
                  </div>
                  {selectedInterviewDetails ? (
                    <div className="mt-2 space-y-1 text-xs text-gray-600">
                      <p>Schedule: {formatInterviewSlot(selectedInterviewDetails)}</p>
                      {selectedInterviewDetails.location && <p>Location: {selectedInterviewDetails.location}</p>}
                      {selectedInterviewDetails.panel && selectedInterviewDetails.panel.length > 0 && (
                        <p>Panel: {selectedInterviewDetails.panel.join(', ')}</p>
                      )}
                      {selectedInterviewDetails.meeting_link && (
                        <a
                          href={selectedInterviewDetails.meeting_link}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-teal-600 underline"
                        >
                          Join Meeting
                        </a>
                      )}
                      {selectedInterviewDetails.notes && (
                        <p className="text-gray-500">Notes: {selectedInterviewDetails.notes}</p>
                      )}
                    </div>
                  ) : (
                    <p className="mt-2 text-xs text-gray-500">No interview scheduled yet.</p>
                  )}
                </div>
                {/* Status Management Actions */}
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide mb-2">Application Status</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedApplicant.status === 'applied' && (
                      <Button 
                        size="sm" 
                        className="flex-1 bg-amber-600 hover:bg-amber-700"
                        onClick={() => handleAcceptApplication(selectedApplicant.id)}
                      >
                        <Icon name="visibility" size={16} className="mr-1" />
                        Move to Review
                      </Button>
                    )}
                    {selectedApplicant.status === 'review' && (
                      <>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-purple-600 hover:bg-purple-700"
                          onClick={() => handleMoveToInterview(selectedApplicant.id)}
                        >
                          <Icon name="calendar_today" size={16} className="mr-1" />
                          Move to Interview
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 border-green-300 text-green-600 hover:bg-green-50"
                          onClick={() => handleMoveToOffer(selectedApplicant.id)}
                        >
                          <Icon name="check_circle" size={16} className="mr-1" />
                          Move to Offer
                        </Button>
                      </>
                    )}
                    {selectedApplicant.status === 'interview' && (
                      <>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-green-600 hover:bg-green-700"
                          onClick={() => handleMoveToOffer(selectedApplicant.id)}
                        >
                          <Icon name="check_circle" size={16} className="mr-1" />
                          Move to Offer
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          className="flex-1 border-amber-300 text-amber-600 hover:bg-amber-50"
                          onClick={() => handleUpdateApplicationStatus(selectedApplicant.id, 'review')}
                        >
                          <Icon name="arrow_back" size={16} className="mr-1" />
                          Back to Review
                        </Button>
                      </>
                    )}
                    {selectedApplicant.status === 'offer' && (
                      <div className="w-full px-3 py-2 rounded-lg bg-green-100 border border-green-300">
                        <div className="flex items-center gap-2 text-green-700">
                          <Icon name="check_circle" size={16} />
                          <span className="text-sm font-semibold">Offer Sent</span>
                        </div>
                      </div>
                    )}
                    {selectedApplicant.status === 'rejected' && (
                      <div className="w-full px-3 py-2 rounded-lg bg-red-100 border border-red-300">
                        <div className="flex items-center gap-2 text-red-700">
                          <Icon name="close" size={16} />
                          <span className="text-sm font-semibold">Application Rejected</span>
                        </div>
                      </div>
                    )}
                    {selectedApplicant.status !== 'rejected' && (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1 border-red-300 text-red-600 hover:bg-red-50"
                        onClick={() => handleRejectApplication(selectedApplicant.id)}
                      >
                        <Icon name="close" size={16} className="mr-1" />
                        Reject
                      </Button>
                    )}
                  </div>
                </div>
                <div className="space-y-2">
                  <p className="text-xs text-gray-500 uppercase tracking-wide">Internal Notes</p>
                  <div className="space-y-2">
                    {notes.map((item) => (
                      <div key={item} className="rounded-2xl border border-gray-100 px-3 py-2 text-gray-700 text-xs">
                        {item}
                      </div>
                    ))}
                  </div>
                  <textarea
                    value={noteDraft}
                    onChange={(event) => setNoteDraft(event.target.value)}
                    placeholder="Add a note for the hiring team..."
                    className="w-full rounded-2xl border border-gray-200 bg-white px-3 py-2 text-xs text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  />
                  <Button size="sm" variant="secondary" className="w-full" onClick={handleAddNote}>
                    Save Note
                  </Button>
                </div>
              </div>
            ) : (
              <p className="text-gray-500">Select a candidate to view profile</p>
            )}
          </div>
        </section>

        {/* Resume parser / ATS insights */}
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Resume Parser & ATS Insights</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Overall ATS Score</p>
                <p className="text-4xl font-bold text-teal-600 mt-2">{selectedApplicant?.ats_score ?? 86}%</p>
                <p className="text-xs text-gray-500 mt-1">Matched against JD keywords</p>
              </div>
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Skill Match</p>
                <p className="text-3xl font-bold text-teal-600 mt-1">89%</p>
                <p className="text-xs text-gray-500 mt-2">Trending skills: React, System Design, Figma</p>
              </div>
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Keyword Ranking</p>
                <p className="text-sm text-gray-700 mt-1">UX Research • Accessibility • Design Systems</p>
              </div>
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Experience Match</p>
                <p className="text-3xl font-bold text-teal-600 mt-1">92%</p>
                <ul className="text-xs text-gray-500 mt-2 list-disc ml-4">
                  <li>Leadership experience aligned</li>
                  <li>Matches required industries</li>
                </ul>
              </div>
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Strengths</p>
                <ul className="text-sm mt-1 list-disc ml-4 text-emerald-600">
                  <li>Deep SaaS experience</li>
                  <li>Leadership roles</li>
                </ul>
              </div>
              <div className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                <p className="text-xs text-gray-500">Gaps</p>
                <ul className="text-sm mt-1 list-disc ml-4 text-amber-600">
                  <li>No fintech background</li>
                  <li>Limited Android exposure</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Interview Management */}
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Interview Management</h2>
              <Button size="sm" onClick={() => openInterviewModal()} disabled={!selectedApplicant}>
                Schedule Interview
              </Button>
            </div>
            <div className="flex flex-wrap gap-2 text-xs mb-4">
              <Button
                size="sm"
                variant="secondary"
                disabled={!selectedApplicant?.candidate_email}
                onClick={handleSendInviteMail}
              >
                Send Invite Mail
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!selectedInterviewDetails?.meeting_link}
                onClick={handleCopyMeetingLink}
              >
                Copy Meet/Zoom Link
              </Button>
              <Button
                size="sm"
                variant="outline"
                disabled={!selectedApplicant}
                onClick={() => openInterviewModal()}
              >
                Assign Panel / Edit
              </Button>
            </div>
            <div className="space-y-4 text-sm">
              {interviewSchedule.map((slot) => (
                <div key={slot.id} className="border border-gray-100 rounded-2xl p-4">
                  <p className="font-semibold">{slot.candidate}</p>
                  <p className="text-gray-500">{slot.role}</p>
                  <p className="text-xs text-gray-400 mt-1">{slot.time}</p>
                  <p className="text-xs text-gray-400">Panel: {slot.panel.join(', ')}</p>
                  {slot.meetingLink && (
                    <a
                      href={slot.meetingLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-xs text-teal-600 underline mt-1 inline-block"
                    >
                      Join Link
                    </a>
                  )}
                  <span className="mt-2 inline-flex rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-600">
                    {slot.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Talent pool & messaging */}
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-bold">Talent Pool</h2>
              <Button size="sm" variant="secondary">View All</Button>
            </div>
            <div className="space-y-3 text-sm">
              {talentPool.map((candidate) => (
                <div key={candidate.name} className="flex items-center justify-between border border-gray-100 rounded-2xl px-4 py-3">
                  <div>
                    <p className="font-semibold">{candidate.name}</p>
                    <p className="text-xs text-gray-500">{candidate.role}</p>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-teal-600">
                    {candidate.tags.map((tag) => (
                      <span key={tag} className="rounded-full bg-teal-50 px-3 py-1">{tag}</span>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Messaging</h2>
            <div className="space-y-3 text-sm">
              <div className="space-y-2">
                <div className="flex flex-wrap gap-2">
                  <Button 
                    size="sm" 
                    variant="secondary"
                    onClick={() => selectedApplicant && handleOpenChat({
                      candidate: selectedApplicant.candidate_name,
                      candidateEmail: selectedApplicant.candidate_email,
                      role: selectedApplicant.job_title,
                    })}
                    disabled={!selectedApplicant}
                  >
                    Send Message
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => selectedApplicant && handleSendOffer({
                      candidate: selectedApplicant.candidate_name,
                    })}
                    disabled={!selectedApplicant}
                  >
                    Send Offer
                  </Button>
                  <Button 
                    size="sm" 
                    variant="outline"
                    onClick={() => selectedApplicant && handleRequestDocuments({
                      candidate: selectedApplicant.candidate_name,
                    })}
                    disabled={!selectedApplicant}
                  >
                    Request Documents
                  </Button>
                </div>
                <Input
                  placeholder={selectedApplicant ? `Quick reply to ${selectedApplicant.candidate_name}...` : "Select a candidate first..."}
                  value={quickMessage}
                  onChange={(event) => setQuickMessage(event.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendQuickMessage()}
                  disabled={!selectedApplicant}
                />
                <Button 
                  size="sm" 
                  className="w-full" 
                  onClick={handleSendQuickMessage}
                  disabled={!selectedApplicant || !quickMessage.trim()}
                >
                  Send
                </Button>
              </div>
              <div className="max-h-96 overflow-y-auto space-y-2">
                {messagingThreads.map((thread, idx) => (
                  <div 
                    key={`${thread.candidate}-${idx}`}
                    className={`rounded-2xl border px-4 py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                      thread.unread 
                        ? 'bg-teal-50 border-teal-500/30' 
                        : 'border-gray-100'
                    }`}
                    onClick={() => handleOpenChat(thread)}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-semibold">{thread.candidate}</p>
                          {thread.unread && thread.unreadCount && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-red-500 text-xs font-bold text-white">
                              {thread.unreadCount}
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{thread.role}</p>
                        <p className={`text-sm mt-1 line-clamp-1 ${thread.unread ? 'font-medium text-gray-900' : 'text-gray-700'}`}>
                          {thread.snippet}
                        </p>
                      </div>
                      {thread.unread && (
                        <div className="flex flex-col items-end gap-1">
                          <span className="w-2 h-2 bg-teal-600 rounded-full animate-pulse"></span>
                          <span className="text-xs text-teal-600 font-medium">New</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Courses / Company profile / Team access */}
        <section className="grid gap-6 lg:grid-cols-3">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Upskilling & Courses</h2>
            <div className="space-y-3 text-sm">
              {hrCourses.map((course) => (
                <div key={course.title} className="border border-gray-100 rounded-2xl px-4 py-3">
                  <p className="font-semibold">{course.title}</p>
                  <p className="text-xs text-gray-500">{course.status} · {course.progress}</p>
                  <Button size="sm" className="mt-2 w-full">Recommend to candidate</Button>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Company Profile</h2>
            <div className="flex items-center gap-3 mb-4">
              <img src={companyProfileData.logo} alt="logo" className="h-14 w-14 rounded-2xl border border-gray-100 object-cover" />
              <div>
                <p className="font-semibold">{companyProfileData.name}</p>
                <a href={companyProfileData.website} className="text-xs text-teal-600" target="_blank" rel="noreferrer">
                  {companyProfileData.website}
                </a>
              </div>
            </div>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Billing Address</p>
            <p className="text-sm mb-4">{companyProfileData.address}</p>
            <p className="text-xs text-gray-500 uppercase tracking-wide">Hiring Managers</p>
            <ul className="text-sm list-disc ml-4 space-y-1">
              {companyProfileData.managers.map((manager) => (
                <li key={manager}>{manager}</li>
              ))}
            </ul>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Team Access</h2>
            <div className="space-y-3 text-sm">
              {teamMembers.map((member) => (
                <div key={member.name} className="flex items-center justify-between border border-gray-100 rounded-2xl px-4 py-3">
                  <div>
                    <p className="font-semibold">{member.name}</p>
                    <p className="text-xs text-gray-500">{member.role}</p>
                  </div>
                  <span className="rounded-full bg-teal-50 px-3 py-1 text-xs font-semibold text-teal-600">
                    {member.access}
                  </span>
                </div>
              ))}
            </div>
            <Button size="sm" variant="secondary" className="w-full mt-4">Add Team Member</Button>
          </div>
        </section>

        {/* Reports & settings */}
        <section className="grid gap-6 lg:grid-cols-2">
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Reports & Analytics</h2>
            <div className="grid gap-4 md:grid-cols-2">
              {analyticsStats.map((stat) => (
                <div key={stat.label} className="rounded-2xl border border-gray-100 p-4 bg-gray-50">
                  <p className="text-xs text-gray-500">{stat.label}</p>
                  <p className="text-2xl font-bold mt-1">{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
          <div className="rounded-xl bg-white p-6 shadow-sm border border-gray-200">
            <h2 className="text-xl font-bold mb-4">Settings & Preferences</h2>
            <div className="space-y-3 text-sm">
              <div className="flex items-center justify-between border border-gray-100 rounded-2xl px-4 py-3">
                <div>
                  <p className="font-semibold text-gray-900">Email Notifications</p>
                  <p className="text-xs text-gray-500">ATS updates, interviews, offers</p>
                </div>
                <Button size="sm" variant="secondary">Manage</Button>
              </div>
              <div className="flex items-center justify-between border border-gray-100 rounded-2xl px-4 py-3">
                <div>
                  <p className="font-semibold text-gray-900">Theme</p>
                  <p className="text-xs text-gray-500">Light / Dark</p>
                </div>
                <Button size="sm" variant="outline">Toggle</Button>
              </div>
              <div className="flex items-center justify-between border border-gray-100 rounded-2xl px-4 py-3">
                <div>
                  <p className="font-semibold text-gray-900">API Keys</p>
                  <p className="text-xs text-gray-500">Webhook & integrations</p>
                </div>
                <Button size="sm" variant="secondary" onClick={async () => {
                  setApiKeyModalOpen(true);
                  setApiKeyLoading(true);
                  try {
                    const hrEmail = localStorage.getItem('dutlaunch_email');
                    const response = await fetch(`https://aptirix-version-610696728606.asia-south1.run.app/api/hr/api-key?hr_email=${hrEmail}`, { headers: buildAuthHeaders() });
                    const data = await response.json();
                    setApiKeyData(data);
                  } catch (error) {
                    console.error('Error fetching API key:', error);
                  } finally {
                    setApiKeyLoading(false);
                  }
                }}>View</Button>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Job Edit Modal */}
      {editingJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeEditJobModal}>
          <div 
            className="relative w-full max-w-2xl rounded-3xl bg-white p-6 shadow-xl max-h-[90vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold">Edit Job Post</h2>
              <button
                onClick={closeEditJobModal}
                className="rounded-lg p-2 hover:bg-gray-100"
              >
                <Icon name="close" size={24} />
              </button>
            </div>

            <form className="space-y-4" onSubmit={handleUpdateJob}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Job Title
                  </label>
                  <Input
                    placeholder="Job title"
                    value={editJobForm.title}
                    onChange={(event) => handleEditJobFormChange('title', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location
                  </label>
                  <Input
                    placeholder="Location (e.g., Bengaluru, India)"
                    value={editJobForm.location}
                    onChange={(event) => handleEditJobFormChange('location', event.target.value)}
                  />
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Employment Type
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                    value={editJobForm.employmentType}
                    onChange={(event) => handleEditJobFormChange('employmentType', event.target.value)}
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Internship">Internship</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Work Type
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                    value={editJobForm.workType}
                    onChange={(event) => handleEditJobFormChange('workType', event.target.value)}
                  >
                    <option value="on-site">On-site</option>
                    <option value="hybrid">Hybrid</option>
                    <option value="remote">Remote</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Experience Level
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                    value={editJobForm.experienceLevel}
                    onChange={(event) => handleEditJobFormChange('experienceLevel', event.target.value)}
                  >
                    <option value="entry">Entry</option>
                    <option value="mid">Mid</option>
                    <option value="senior">Senior</option>
                  </select>
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Department / Team
                  </label>
                  <Input
                    placeholder="Department / Team"
                    value={editJobForm.department}
                    onChange={(event) => handleEditJobFormChange('department', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Category
                  </label>
                  <select
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                    value={editJobForm.category}
                    onChange={(event) => handleEditJobFormChange('category', event.target.value)}
                  >
                    <option value="IT">IT</option>
                    <option value="Non-IT">Non-IT</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tags (comma separated)
                  </label>
                  <Input
                    placeholder="Tags (comma separated)"
                    value={editJobForm.tags}
                    onChange={(event) => handleEditJobFormChange('tags', event.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Job Description (Text) *
                </label>
                <textarea
                  className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-teal-500/40"
                  rows={6}
                  placeholder="Role description, responsibilities, skills..."
                  value={editJobForm.description}
                  onChange={(event) => handleEditJobFormChange('description', event.target.value)}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Job Description PDF (Optional)
                  <span className="text-xs text-gray-500 ml-2">- ATS will compare Resume PDF with JD PDF</span>
                </label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                  <input
                    type="file"
                    accept=".pdf,application/pdf"
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      if (file) {
                        if (file.size > 5 * 1024 * 1024) {
                          setEditJobError('JD PDF file size must be less than 5MB.');
                          event.target.value = '';
                          return;
                        }
                        if (file.type !== 'application/pdf' && !file.name.toLowerCase().endsWith('.pdf')) {
                          setEditJobError('Please upload a PDF file only.');
                          event.target.value = '';
                          return;
                        }
                        handleEditJobFormChange('jdPdfFile', file);
                        setEditJobError('');
                      }
                    }}
                    className="w-full text-sm text-gray-600"
                  />
                  {editJobForm.jdPdfFile && (
                    <p className="mt-2 text-sm text-green-600">
                      <Icon name="check_circle" size={16} className="inline mr-1" />
                      {editJobForm.jdPdfFile.name} selected
                    </p>
                  )}
                  {editingJob?.jd_pdf_url && !editJobForm.jdPdfFile && (
                    <p className="mt-2 text-sm text-gray-600">
                      <Icon name="description" size={16} className="inline mr-1" />
                      Current JD PDF is attached. Upload a new file to replace it.
                    </p>
                  )}
                  <p className="mt-1 text-xs text-gray-500">
                    Upload JD as PDF for better ATS scoring. If both text and PDF are provided, PDF will be used for ATS calculation.
                  </p>
                </div>
              </div>

              {editJobError && <p className="text-sm text-red-500">{editJobError}</p>}

              <div className="flex flex-wrap gap-3 pt-4">
                <Button type="submit" size="sm" disabled={editJobSubmitting}>
                  {editJobSubmitting ? 'Updating...' : 'Update Job'}
                </Button>
                <Button type="button" size="sm" variant="outline" onClick={closeEditJobModal}>
                  Cancel
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Interview Scheduling Modal */}
      {interviewModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-3xl w-full mx-4 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Schedule Interview
                </h3>
                {selectedApplicant && (
                  <p className="text-sm text-gray-500">
                    {selectedApplicant.candidate_name} · {selectedApplicant.job_title || 'Role'}
                  </p>
                )}
              </div>
              <button type="button" onClick={closeInterviewModal} className="text-gray-500 hover:text-gray-700">
                <Icon name="close" size={20} />
              </button>
            </div>
            <form className="space-y-4" onSubmit={handleSaveInterview}>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Interview Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={interviewForm.date}
                    onChange={(event) => handleInterviewFormChange('date', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Time *
                  </label>
                  <input
                    type="time"
                    required
                    value={interviewForm.startTime}
                    onChange={(event) => handleInterviewFormChange('startTime', event.target.value)}
                    className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700"
                  />
                </div>
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Timezone
                  </label>
                  <Input
                    placeholder="Timezone"
                    value={interviewForm.timezone}
                    onChange={(event) => handleInterviewFormChange('timezone', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Meeting Platform
                  </label>
                  <Input
                    placeholder="Google Meet, Zoom..."
                    value={interviewForm.meetingPlatform}
                    onChange={(event) => handleInterviewFormChange('meetingPlatform', event.target.value)}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Meeting Link *
                </label>
                <Input
                  placeholder="https://meet.google.com/..."
                  value={interviewForm.meetingLink}
                  onChange={(event) => handleInterviewFormChange('meetingLink', event.target.value)}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Panel Members (comma separated)
                </label>
                <Input
                  placeholder="Ananya Rao, Suresh Kumar"
                  value={interviewForm.panel}
                  onChange={(event) => handleInterviewFormChange('panel', event.target.value)}
                />
              </div>
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Location / Studio
                  </label>
                  <Input
                    placeholder="Google Meet / Bengaluru HQ"
                    value={interviewForm.location}
                    onChange={(event) => handleInterviewFormChange('location', event.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Notes
                  </label>
                  <Input
                    placeholder="Share documents before interview..."
                    value={interviewForm.notes}
                    onChange={(event) => handleInterviewFormChange('notes', event.target.value)}
                  />
                </div>
              </div>
              {interviewError && <p className="text-sm text-red-500">{interviewError}</p>}
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={closeInterviewModal}>
                  Cancel
                </Button>
                <Button type="submit" disabled={interviewSaving}>
                  {interviewSaving ? 'Saving…' : 'Save & Send'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirmJob && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Icon name="warning" className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Delete Job Post
                </h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete the job post <strong className="text-gray-900">"{deleteConfirmJob.title}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                All applications and data associated with this job will be permanently removed.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={cancelDeleteJob}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDeleteJob}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                <Icon name="delete" size={16} className="mr-2" />
                Delete Job
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Application Confirmation Modal */}
      {deleteConfirmApplication && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4 border border-gray-200">
            <div className="flex items-center gap-4 mb-4">
              <div className="p-3 bg-red-100 rounded-full">
                <Icon name="warning" className="text-red-600" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900">
                  Delete Application
                </h3>
                <p className="text-sm text-gray-500">
                  This action cannot be undone
                </p>
              </div>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700">
                Are you sure you want to delete the application from <strong className="text-gray-900">"{deleteConfirmApplication.candidate_name}"</strong>?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                This application will be permanently removed from the system and cannot be recovered.
              </p>
            </div>

            <div className="flex gap-3 justify-end">
              <Button
                type="button"
                variant="outline"
                onClick={cancelDeleteApplication}
                className="px-4 py-2"
              >
                Cancel
              </Button>
              <Button
                type="button"
                onClick={confirmDeleteApplication}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white border-red-600"
              >
                <Icon name="delete" size={16} className="mr-2" />
                Delete Application
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Chat Modal */}
      {chatModalOpen && selectedChatCandidate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-4 h-[600px] flex flex-col border border-gray-200">
            {/* Chat Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                  <Icon name="person" size={24} className="text-blue-600" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900">{selectedChatCandidate.candidate}</h3>
                  <p className="text-sm text-gray-500">{selectedChatCandidate.role}</p>
                </div>
              </div>
              <button
                onClick={() => setChatModalOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-full transition-colors"
              >
                <Icon name="close" size={24} className="text-gray-500" />
              </button>
            </div>

            {/* Chat Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {chatMessages.length === 0 && (
                <div className="text-center text-gray-500 py-8">
                  <Icon name="chat_bubble_outline" size={48} className="mx-auto mb-2 opacity-50" />
                  <p>No messages yet. Start the conversation!</p>
                </div>
              )}
              {chatMessages.map((message) => (
                <div
                  key={message.id}
                  className={`flex ${message.sender === 'HR' ? 'justify-end' : 'justify-start'}`}
                >
                  <div
                    className={`max-w-[70%] rounded-2xl px-4 py-2 ${
                      message.sender === 'HR'
                        ? 'bg-teal-600 text-white'
                        : 'bg-gray-100 text-gray-900'
                    }`}
                  >
                    <p className="text-sm">{message.text}</p>
                    <p className={`text-xs mt-1 ${message.sender === 'HR' ? 'text-white/70' : 'text-gray-500'}`}>
                      {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Chat Input */}
            <div className="p-4 border-t border-gray-200">
              <div className="flex gap-2 mb-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setChatInput('We would like to offer you the position. Please review the attached offer letter.');
                  }}
                >
                  <Icon name="mail" size={16} className="mr-1" />
                  Send Offer
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => {
                    setChatInput('Could you please share your updated resume and portfolio?');
                  }}
                >
                  <Icon name="description" size={16} className="mr-1" />
                  Request Docs
                </Button>
                <label className="cursor-pointer">
                  <Button size="sm" variant="outline" as="span">
                    <Icon name="attach_file" size={16} className="mr-1" />
                    Attach
                  </Button>
                  <input type="file" className="hidden" onChange={(e) => {
                    if (e.target.files?.[0]) {
                      alert(`File "${e.target.files[0].name}" attached (mock)`);
                    }
                  }} />
                </label>
              </div>
              <div className="flex gap-2">
                <Input
                  placeholder="Type your message..."
                  value={chatInput}
                  onChange={(e) => setChatInput(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendChatMessage()}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendChatMessage}
                  disabled={!chatInput.trim()}
                  className="px-6"
                >
                  <Icon name="send" size={18} />
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* API Key Modal */}
      {apiKeyModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setApiKeyModalOpen(false)}>
          <div 
            className="relative w-full max-w-2xl rounded-3xl bg-white p-8 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => setApiKeyModalOpen(false)}
              className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
            >
              <span className="material-icons">close</span>
            </button>

            <h2 className="text-2xl font-bold text-gray-900 mb-6">Your API Key</h2>

            {apiKeyLoading ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500 mx-auto"></div>
                <p className="mt-4 text-gray-600">Loading...</p>
              </div>
            ) : apiKeyData && apiKeyData.has_api_key ? (
              <div className="space-y-6">
                <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">API Key</h3>
                    <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                      apiKeyData.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                    }`}>
                      {apiKeyData.is_active ? '● Active' : '○ Inactive'}
                    </span>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 mb-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium text-gray-600">Your API Key</span>
                      <button
                        onClick={() => setApiKeyVisible(!apiKeyVisible)}
                        className="text-sm text-teal-600 hover:underline flex items-center gap-1"
                      >
                        <span className="material-icons text-sm">{apiKeyVisible ? 'visibility_off' : 'visibility'}</span>
                        {apiKeyVisible ? 'Hide' : 'Show'}
                      </button>
                    </div>
                    <div className="font-mono text-sm bg-gray-50 p-3 rounded border border-gray-200 break-all">
                      {apiKeyVisible ? apiKeyData.api_key : '••••••••••••••••••••••••••••••••••••••••••••••••'}
                    </div>
                  </div>

                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(apiKeyData.api_key);
                      alert('✓ API key copied to clipboard!');
                    }}
                    className="w-full bg-teal-600 text-white py-3 rounded-lg hover:bg-teal-600/90 transition font-medium flex items-center justify-center gap-2"
                  >
                    <span className="material-icons text-sm">content_copy</span>
                    Copy API Key
                  </button>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Company</p>
                    <p className="font-semibold text-gray-900">{apiKeyData.company_name}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">CIN Number</p>
                    <p className="font-semibold text-gray-900">{apiKeyData.cin_number}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Usage Count</p>
                    <p className="font-semibold text-gray-900">{apiKeyData.usage_count} requests</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-600 mb-1">Created</p>
                    <p className="font-semibold text-gray-900">{new Date(apiKeyData.created_at).toLocaleDateString()}</p>
                  </div>
                </div>

                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                  <p className="text-sm text-yellow-800">
                    <strong>⚠️ Important:</strong> Keep your API key secure. Use it in the X-API-Key header when posting jobs via API.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="material-icons text-gray-400 text-4xl">key_off</span>
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No API Key Found</h3>
                <p className="text-gray-600 mb-6">Contact your administrator to generate an API key for your account.</p>
              </div>
            )}
          </div>
        </div>
      )}
    </HrDashboardLayout>
  );
};

export default HrDashboardPage;

